// ListService,js
import objectBehaviorMap from 'ObjectBehaviorMapService.js';
import _ from 'lodash';
import Immutable        from 'immutable';
import util             from 'util/Util.js';
import objectUtil       from 'util/ObjectUtil.js';
import UndoManager      from 'undo-manager';
import constants        from 'Constants.js';
import factoryService   from 'FactoryService.js';
import pageInstanceFactory from 'PageInstanceFactory';
import {Model}          from 'backbone';
import sequenceService  from 'SequenceService.js';
import undoService          from 'UndoService.js'
import focusController      from 'FocusController';
import eventBus from 'EventBus.js';
import pageSyncService      from 'PageSyncService';
import ConnectionVO from 'ConnectionVO.js';
import textConstants        from 'TextConstants';
import multiSelectControlModel from 'MultiSelectControlModel';
import reportingMetaData from 'ReportingMetaDataModel'
import GroupControlView from 'common-visuals/GroupControlView.js';
import BehaviorInstanceModel    from 'BehaviorInstanceModel.js';
import CommandGenericView from 'behaviors/CommandGenericView.js';
import alignmentController      from 'AlignmentController.js';
import utilMobile from './../mobile/commons/MobileGeneralFunctions';
import mobileConstants from '../mobile/commons/MobileFormsConstants';
import mobileListService from '../service/mobile/MobileListService';
import $ from "jquery";
import Spinner from 'spin/spin';
import notificationService from "../service/NotificationService";
import tableService from 'TableService';
import userService from 'UserService';

//====================================================================================================================
// This is the anchor of all STATE.
//
// An exported singleton for holding object instances.
// E.g. control instances, workflows, behaviors, ..
//
// - Lou Mauget, November 19, 2015
//
//--------------------------------------------------------------------------------------------------------------------
// Sidebar:
//
// These data structures do not model the structure of a saved or loaded XML payload. This loose coupling
// means the implementation of state for rendering and user interaction is independent of payload format.
//
// A bridge maps the world of the designer editor to that of a REST service payload. The XJService is the bridge
// to and from the payload. -Lou Mauget, March 3, 2016.
//
//--------------------------------------------------------------------------------------------------------------------
// Notes:
//
// The service largely consists of immutable 'Map' data structures.
//
// + A Map resembles a JavaScript array of objects.
// + Each item is a key-value pair, where either side can be a JS object.
// + Map == hash == hash table == object-indexed array.
// + A Map is an interable that has get, first, last, and map (lowercase name) methods, among others.
// + ListService exposes some Maps as get-only properties.
// + A caller can directly modify a Map, but the central ListService returns a copy.
// + A caller may modify a Map via listservice methods (e.g. add), but it must access the Map via get to see any
//     modification in any local copy of the Map.
// + Our key is an ID of a Backbone View visual object.
// + A key is a DOM ID in the case of an HTML object
// + A key is simply an assigned BackBone ID of a behavior or workflow vector-drawn canvas object.
//
//--------------------------------------------------------------------------------------------------------------------
// Advantages of immutability
//
// + Defensive copying: no need to clone state data for DEFENSE against a bad class of BUGS.
// + Enables writing referentially transparent client functions that provide a REPEATABLE result for a given input.
// + Equality testing: if two references to a immutable structure are equal, then the contents are unchanged.
//     (deep changes still possible unless deep data is also immutable)
// + Undo-redo: state maintained in an immutable stack of immutable data structures can be played forward ot backward.
//     ( Legacy Perfect Forms Editor has an extensive undo-redo capability )
//
//--------------------------------------------------------------------------------------------------------------------
// HOW TO use this service.
//
// o This application leverages ES6 and Immutable.js.
// o The listService is a singleton that holds a single source of state truth for the application instance.
// o The module exports only an instance of ListService.
// o The module capability of Webpack ensures that only one single exported instance imports into all consuming modules.
// o In all cases, this module holds state; it is used by UI code, but knows nothing of UI.
//
//--------------------------------------------------------------------------------------------------------------------
// Loaded Perfect Forms form:
//
// + All control object instances in a designer session are stored as Backbone Views in the objMap.
// + Pages, and the single form, are also Backbone view objects stored in the objMap.
// + Each control is keyed by its DOM / Backbone ID.
// + All Page object instances are additionally stored as names keyed by a DOM / Backbone ID
// + Each control has a pageId property.
// + Each control MAY have a groupId property.
//
//--------------------------------------------------------------------------------------------------------------------
// Object list and page list:
//
// Example 1 - Log all control iDs assigned to data page 'PF_2':
//
//   listService.getPageControls( 'PF_2' ).map( (v, k) => console.log(k) );
//
// Example 2 - Log the Perfect Forms name value for DOMID 'PF_4':
//
//   console.log( listService.getObject( 'PF_4' ).model.get( 'name' ) );
//
// Example 3 - Log all form data page titles in the system
//
//   console.log( listService.pagetList.map( (v, k) => v.model.get( 'pageTitle' ) ));
//
//--------------------------------------------------------------------------------------------------------------------
// Selection mechanism:
//
// o The listService records selected controls by storing their references in seleected, another Map property.
// o The focusController is the main orchestrator of listService selected state.
// o listService only records selected state; the caller must draw selection borders; see focusService.
//
// Example 4 - Log all selected control IDs:
//
//   listService.selected.map( (v, k) => console.log( k ) );
//
// Example 5 - reset entire selection
//
//   listService.clearSelected();
//
//====================================================================================================================
class ListService {

	constructor() {

        // Each object is a live Backbone view of a PageInstanceView. Key is its domId.
		this._pageList 	    = Immutable.OrderedMap();

        // Each object is a live BackBone view of a control. Key is its domId. E.g. 'PF_12'
        // Each control view model has a pageId key to its page in the pageList.
		this._objMap 	    = Immutable.OrderedMap();

		// Contains the mobile controls.
		this._mobileObjMap 	 = Immutable.OrderedMap();

        // Workflow divided into five aspects corresponding roughly to top-level XML structure.
        this._workflow = {
            properties: Immutable.Map(),
            comments:   Immutable.Map(),
            stages:     Immutable.OrderedMap(),
            paths:      Immutable.Map(),
            roles:      Immutable.OrderedMap()
        };

        //PF-11196 Stores the translations for multilanguage
        this._multilanguage = {
            languages: [],
            translations:  {}
        };
        this._currentLanguage = 'default';
        this._isMultilanguageDirty = false;

        //PF-11452
        this._advancedCSS = {};
        this._isAdvancedCSSDirty = false;
        this._isAdvancedCSSEnabled = false;

        //PF-11402 Stores the custom code blocks
        this._codeBlocks = [];

        // Dom ID of selected control on layout. Key to objMap
		this._currentId 	= '';

        // Dom ID of current page occupying the visual layout. Key to pageList
		this._currentPageId = '';

        // Map/hash of domId: control view of visually selected objects in objMap
        this._selected      = Immutable.Map();

         // Bool to check if a column in a table is selected
         this._isTableColumnSelected = false;

        // Map/hash of domId: control view of clipboard objects on the "clipboard"
        // Note: this IS the clipboard; it's not some OS-provided API
        this._clipboard     = Immutable.Map();

        // Warning: behavior clipboard not implemented same as control clipboard
        this._behaviorClipboard  = null;

        // Stub for future memoized undo/redo list
        this._undoManager   = new UndoManager();

        // A cache of main views for: layout, behaviors, workflow, and reports
        this._mainViews     = Immutable.Map();

        // ID of which kind of main view displayed: L, B, W, R.
        this._mainKind      = '';

        // Distinguish doc type: Current valid types are DOC_TYPE_FORM or DOC_TYPE_REPORT.
        this._docType = constants.DOC_TYPE_FORM;

        // Probably deprecated. Used for right-left arrow nav among L, B, or W in original desktop.
        this._mainHistory   = Immutable.Stack();

        // Cached HTML5 canavs. Fluff.
        this._fcanvas       = undefined;

        this._currentForm   = undefined;
        this._formBase      = {};

        this._isDirty = false;
        this._isDirtyRecovery = false;

        this._snapToGrid = false;

        this._snapToNeighbor = false;

        this._isDrawingMultiSelection = false;

        this._isFromMultSelectionGroup = false;

        this._isResizing = false;

        this._isDraggingCp = false;

        // ------------- Behavior stuff. An instance (script) ties to a control. It has commands
        this._behaviorInstanceList      = Immutable.OrderedMap();

        this._currentBehaviorInstanceId = '';

        this._orderedActiveBehaviors = [];

        // Behavior  commands (eg. script commands)
        this._behaviorObjMap  = Immutable.Map();

        this._behaviorConnections = Immutable.Map();

        this._dataSources = Immutable.Map();

        this._dependencies = Immutable.Map();
        this._reportingMetaData = Immutable.Map();

        // ------------ End Behavior stuff

        this._cleanIds = Immutable.Map();

        this._currentRole = 'all';
        this._currentStage = 'all';

        this._userProfile = {};
        this._userSettings = {};
        this._recoverySaveInterval;
        this._activeSession = -1;
        this._activeSessionRetries = 0;
        this._activeSessionRetriesTimeout = constants.SESSION_TIMEOUT_MS;

        this._dashboardLayout = {
            leftPanelView: undefined,
            mainPanelView: undefined
        };

        this._loading = false;
        this._isElementMoving = false;
        this._elementWasDropped = false;
        this._firstLoad = false;

        this._propertySettingsViews = Immutable.Map();
        this._propertyStyleViews = Immutable.Map();
        this._propertyAccessViews = Immutable.Map();

        this._propertyTab_Attributes;
        this._propertyTab_Behaviors;
        this._propertyTab_Settings;
        this._propertyTab_Style;
        this._propertyTab_Access;
        this._loadProperties = false;

        this._currentBehaviorId = '';

        this._selectFieldExpandedPages = Immutable.Map();
        this._formItemSelected = undefined;
        this._reportItemSelected = undefined;
        this._connectionItemSelected = undefined;
        this._userItemSelected = undefined;
        this._activeDashboardTab = '';

        this._accountSettings;

        this._emailServers;

        this._jsonData;
        this._previousSelected;
        this._isSelectedPage = false;

        this._currentCopiedRadioButtonCount;

        this._isOnExport= false;
        this._wentBackAlready = false;
        this._linkClicked = false;
        this._useBehaviorId = false;
        this._behaviorId = '';
       
        this._itemSearched = 0;

        this._promiseInProgress = [];
        this._previewParameters = undefined;
        this._logoutRedirect = undefined;
        //Mobile
        this._mobilePages = Immutable.Map();
        this._mobileSections = null;
        this._mobileObjects = Immutable.Map();
        this._mobileNewObjects = Immutable.Map();
        this._mobileColumns = Immutable.Map();
        this._mobileColumnSection = Immutable.Map();

        this._objDefaultMobileStyles = {};
        this._mobileGlobalSettings = {};

        this._mobileStyles = Immutable.Map();
        this._mobileSettings = Immutable.Map(this._mobileGlobalSettings);
        this._isMobileOpen = false;
        this._isMobileDirty = false;
        this._mobileCurrentStep = 1;
        this._resetCurrentMobileForm = false;
        this._mobileWizardCompleted = null;
        this._currentObjectDragged = null;
        this._timesWizardWasOpened = 0;
        this._timesWizardCompleted = 0;
        this._controlHtmlMobileSections = Immutable.Map();
        this._mobileCurrentPage = 1;
        this._groupItemHTML = null;
        this._layoutControlHTML = null;
        this.isSaveDialogOpened = false;
        this._mobileVideoHasBeenSeen = false;

        this._avoidDoubleClickOnDashboardTree = false;
        this._dashboardMenuClick = false;
        this._isHandleInsideGroup =  false;
        this._isMouseDown =  false;

        this._isPartialSave =  true;
        this._isRecoverySave = false;
        
        this._gatewayUser =  undefined;

        this._salesEmail =  constants.APP_SALES_EMAIL;

        this._isUndoRedoEnabled = true;
    }

    set isUndoRedoEnabled(value) {
        this._isUndoRedoEnabled = util.isTrue(value);
    }

    get isUndoRedoEnabled() {
        return this._isUndoRedoEnabled;
    }
    
    set gatewayUser(value) {
        this._gatewayUser = value;
    }

    get gatewayUser() {
        return this._gatewayUser;
    }

    set salesEmailAddress(value) {
        this._salesEmail = value;
    }
    get salesEmailAddress() {
        return this._salesEmail;
    }

    set isHandleInsideGroup(value) {
        this._isHandleInsideGroup = value;
    }

    get isHandleInsideGroup() {
        return this._isHandleInsideGroup;
    }    

    set isRecoverySave(value) {
        this._isRecoverySave = value;
    }

    get isRecoverySave() {
        return this._isRecoverySave;
    }

    set isPartialSave(value) {
        this._isPartialSave = value;
    }

    get isPartialSave() {
        return this._isPartialSave;
    }

    set isMouseDown(value) {
        this._isMouseDown = value;
    }

    get isMouseDown() {
        return this._isMouseDown;
    }



    set avoidDoubleClickOnDashboardTree(html) {
        this._avoidDoubleClickOnDashboardTree = html;
    }

    get avoidDoubleClickOnDashboardTree() {
        return this._avoidDoubleClickOnDashboardTree;
    }

    set dashboardMenuClick(val) {
        this._dashboardMenuClick = val;
    }

    get dashboardMenuClick() {
        return this._dashboardMenuClick;
    }

    set groupItemHTML(html) {
        this._groupItemHTML = html;
    }

    get groupItemHTML() {
        return this._groupItemHTML;
    }

    set layoutControlHTML(html) {
        this._layoutControlHTML = html;
    }

    get layoutControlHTML() {
        return this._layoutControlHTML;
    }

    get mobileVideoHasBeenSeen() { return this._mobileVideoHasBeenSeen; }
    set mobileVideoHasBeenSeen( arg ) { this._mobileVideoHasBeenSeen = arg; }

    defaultMobileStyles(){
	    return this._objDefaultMobileStyles;
    }

    defaultMobileSettings(){
        return this._mobileGlobalSettings;
    }

    get mobileStyles() {return this._mobileStyles;}

    getMobileStyles() {return this._mobileStyles;}

    iniMobileStyle(obj){
        this._mobileStyles= Immutable.Map(obj);
    }

    getMobileSetting(key){
        let settings = this._mobileSettings.get(key);
        if(settings){
            return settings;
        }
        return null;
    }

    iniMobileGlobalSettings(obj){
        this._mobileSettings= Immutable.Map(obj);
    }

    //Mobile
    addMobileSetting( id, properties) {
        this._mobileSettings = this._mobileSettings.set( id, properties );
    }

    getMobileStyleByKey(key){
        let styles = this._mobileStyles.get(key);
        if(styles){
            return styles;
        }
        return null;
    }

    updateMobileStyleProperty(key, propertyName, newValue) {
        let style = this.getMobileStyleByKey(key);

        if (style) {
            let updatedStyle = { ...style };
            updatedStyle[propertyName] = newValue;
            this._mobileStyles = this._mobileStyles.set(key, updatedStyle);
        } else {
            console.warn(`updateMobileStyleProperty: Style object not found for key "${key}".`);
        }
    }

    updateFontSizeByKey(key, newFontSize){
        let style = this.getMobileStyleByKey(key);
        style.fontSize = newFontSize;
        this._mobileStyles = this._mobileStyles.set(key, style);
    }

    updateMarginSizeByKey(key, newSize){
        let style = this.getMobileStyleByKey(key);
        style.marginSize = newSize ? newSize : style.marginSize;
        this._mobileStyles = this._mobileStyles.set(key, style);
    }

    updateMarginDefaultByKey(key, defaultMargin){
        let style = this.getMobileStyleByKey(key);
        style.defaultMargin = defaultMargin ? defaultMargin : mobileConstants.DEFAULT_MARGIN;
        this._mobileStyles = this._mobileStyles.set(key, style);
    }
    

    get isOnExport() { return this._isOnExport; }
    set isOnExport( arg ) { this._isOnExport = arg; }

    get wentBackAlready() { return this._wentBackAlready; }
    set wentBackAlready( arg ) { this._wentBackAlready = arg; }

    get linkClicked() { return this._linkClicked; }
    set linkClicked( arg ) { this._linkClicked = arg; }

    get useBehaviorId() { return this._useBehaviorId; }
    set useBehaviorId( arg ) { this._useBehaviorId = arg; }
    
    get behaviorId() { return this._behaviorId; }
    set behaviorId( arg ) { this._behaviorId = arg; }

    get currentCopiedRadioButtonCount() { return this._currentCopiedRadioButtonCount; }
    set currentCopiedRadioButtonCount( arg ) { this._currentCopiedRadioButtonCount = arg; }

    get isDraggingCp() { return this._isDraggingCp; }
    set isDraggingCp(isDragging) { this._isDraggingCp = isDragging; }

    get emailServers() { return this._emailServers; }
    set emailServers( arg ) { this._emailServers = arg; }

    get jsonData() { return this._jsonData; }
    set jsonData( arg ) { this._jsonData = arg; }

	get accountSettings() { return this._accountSettings; }
	set accountSettings( arg ) { this._accountSettings = arg; }

	get selectFieldExpandedPages() { return this._selectFieldExpandedPages; }
	addSelectFieldExpandedPage(id) {
	    this._selectFieldExpandedPages = this._selectFieldExpandedPages.set(id, id);
    }
    removeSelectFieldExpandedPage(id) {
	    this._selectFieldExpandedPages = this._selectFieldExpandedPages.delete(id);
    }

    get propertyTab_Attributes() { return this._propertyTab_Attributes; }
    set propertyTab_Attributes(view) { this._propertyTab_Attributes = view; }

    get propertyTab_Behaviors() { return this._propertyTab_Behaviors; }
    set propertyTab_Behaviors(view) { this._propertyTab_Behaviors = view; }

    get propertyTab_Settings() { return this._propertyTab_Settings; }
    set propertyTab_Settings(view) { this._propertyTab_Settings = view; }

    get propertyTab_Style() { return this._propertyTab_Style; }
    set propertyTab_Style(view) { this._propertyTab_Style = view; }

    get propertyTab_Access() { return this._propertyTab_Access; }
    set propertyTab_Access(view) { this._propertyTab_Access = view; }

    get loadProperties() { return this._loadProperties; }
    set loadProperties(val) { this._loadProperties = val; }

    get itemSearched() { return this._itemSearched;  }
    set itemSearched( id ) { this._itemSearched = id; }
    
    get promiseInProgress() { return this._promiseInProgress; }
    set promiseInProgress( itemId ) { this._promiseInProgress.push(itemId); }

    get previewParameters() { return this._previewParameters; }
    set previewParameters(parameters) { this._previewParameters = parameters; }

    get accountAPIEnabled() { return this._accountAPIEnabled;  }
    set accountAPIEnabled( arg ) { this._accountAPIEnabled = arg; }
    
    get logoutRedirect() { return this._logoutRedirect; }
    set logoutRedirect(logoutRedirect) { this._logoutRedirect = logoutRedirect; }

    removePromiseInProgress(itemId) {
        const index = this._promiseInProgress.indexOf(itemId);
        if ( index >= 0 ) {
            this._promiseInProgress.splice( this._promiseInProgress[index], 1 );
        }
    }

    addPropertyViews(propertyViews, controlId, type) {

	    switch (type) {
	        case constants.PROP_SETTINGS:
                this._propertySettingsViews = this._propertySettingsViews.set(controlId, propertyViews);
                break;
            case constants.PROP_STYLE:
                this._propertyStyleViews = this._propertyStyleViews.set(controlId, propertyViews);
                break;
            case constants.PROP_ACCESS:
                this._propertyAccessViews = this._propertyAccessViews.set(controlId, propertyViews);
                break;
        }

    }
    getPropertyViews(controlId, type) {
	    let views;

	    switch (type) {
            case constants.PROP_SETTINGS:
                views = this._propertySettingsViews.get(controlId);
                break;
            case constants.PROP_STYLE:
                views = this._propertyStyleViews.get(controlId);
                break;
            case constants.PROP_ACCESS:
                views = this._propertyAccessViews.get(controlId);
                break;
        }

        return views;
    }

    get loading() { return this._loading; }
    set loading(arg) { this._loading = arg; }

    get firstLoad() { return this._firstLoad; }
    set firstLoad(arg) { this._firstLoad = arg; }

    get isElementMoving() { return this._isElementMoving; }
    set isElementMoving(status) { this._isElementMoving = status; }

    get elementWasDropped() { return this._elementWasDropped; }
    set elementWasDropped(status) { this._elementWasDropped = status; }

    get previousSelected() { return this._previousSelected; }
    set previousSelected(arr) { this._previousSelected = arr; }

	getCurrentRole(){
	    return this._currentRole;
    }

    setCurrentRole(role){
        this._currentRole = role;
    }

    getCurrentStage(){
        return this._currentStage;
    }

    setCurrentStage(stage){
        this._currentStage = stage;
    }

    //============================================================
    // Behavior (script)
    //============================================================
    addBehaviorInstance( behaviorInstance, onLoad = false) {
        this._behaviorInstanceList = this._behaviorInstanceList.set( behaviorInstance.id, behaviorInstance );
        this._currentBehaviorInstanceId = behaviorInstance.id;
        if (behaviorInstance.get('behaviorKey') !== 'workflow' && !onLoad) {
            //eventBus.trigger(constants.EV_PAGES_UPDATED);
            $(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['add', 'behaviors', behaviorInstance.id, behaviorInstance.get('text'), this._currentPageId, behaviorInstance.id ]);
        }
    }

    getBehaviorInstance( id ) {

        const instance = this._behaviorInstanceList.get(id);
        return instance;
    }

    getBehaviorInstanceByObjectAndBehaviorKey( objectId, behaviorKey) {
        const filter = (instance) => {
            return (instance.get('ownerId') === objectId && instance.get('behaviorKey') === behaviorKey);
        };

        const instances = this._behaviorInstanceList.filter(filter).first();

        return instances;
    }

    getBehaviorInstanceByObject( objectId ) {
        const filter = (instance) => {
            return (instance.get('ownerId') === objectId );
        };

        const instances = this._behaviorInstanceList.filter(filter);

        return instances;
    }

     getBehaviorsByObjectId( id ) {
       let behaviors = [];
       const instances = this.getBehaviorInstanceByObject(id);

       instances.map( (value,key) => {
           const filter = (instance) => {
               return (instance.behaviorInstanceId === key);
           };
           const b = this._behaviorObjMap.filter(filter);
           b.map( (v,k) => {
               behaviors.push(k);
           });
       });
       return behaviors;
    }

    getBehaviorsByInstanceId(id){
        return this._behaviorObjMap.filter( behavior => behavior.behaviorInstanceId === id && behavior.model.get("factoryKey") !== "command_comment");
    }

    getBehaviorInstanceByPage( pageId ) {
        const filter = (instance) => {
            return (instance.get('pageId') === pageId );
        };

        const instances = this._behaviorInstanceList.filter(filter);

        return instances;
    }

    // PF-3515: Correct count of behaviors on thumbs page per page
    getBehaviorTotals( pageId, getFormBehaviors=false ) {
        let behaviorsList = [];
        let filteredInstances;

        if ( getFormBehaviors ) {
            filteredInstances = this._behaviorInstanceList.filter(beh=>{
                return beh.get('ownerId') === 'Form';
            });
        } else {
            filteredInstances = this._behaviorInstanceList.filter(beh=>{
                return beh.get('ownerId') !== 'Form' && beh.get('pageId') === pageId;
            });
        }

        const instances = this._behaviorObjMap.filter((instance)=>{ return filteredInstances.has(instance.behaviorInstanceId); });
        let uniqueInstances = [];

        instances.forEach(inst=>{
            if ( uniqueInstances.indexOf(inst.behaviorInstanceId) === -1 ) {
                uniqueInstances.push(inst.behaviorInstanceId);
                let singleInstance = this._behaviorInstanceList.get(inst.behaviorInstanceId);
                behaviorsList.push({
                    name: singleInstance.attributes.text,
                    link: '',
                    id: singleInstance.attributes.id
                });
            }
        });
        return {size: uniqueInstances.length, behaviors: behaviorsList};
    }

    // PF-3515: Correct count of behaviors on thumbs page for the entire form
    getBehaviorFormTotals() {
        return this.getBehaviorTotals(0, true);
    }

    getOpenBehaviorInstances() {
        const instances = this._behaviorInstanceList.filter( instance => instance.get('open') );

        return instances;
    }

    getCanvasObjectById( id ) {
        let foundObject;
        var objs = this.fcanvas.getObjects();

        for(let x=0; x < objs.length; x++) {
            if (objs[x].model) {
                if (objs[x].model.get('id') === id) {
                    foundObject = objs[x];
                    break;
                }
            }
        }

        return foundObject;
    }

    get activeBehaviorInstances() { return this.getOpenBehaviorInstances();}
    get behaviorInstances() { return this._behaviorInstanceList; }
    
    get currentBehaviorInstanceId() { return this._currentBehaviorInstanceId; }
    set currentBehaviorInstanceId(val) { this._currentBehaviorInstanceId = val; }

    get orderedActiveBehaviors(){ return this._orderedActiveBehaviors; }
    set orderedActiveBehaviors(val){ this._orderedActiveBehaviors = val; }

    get behaviorCommands() { return this._behaviorObjMap; }
    setBehaviorCommands(map){
        this._behaviorObjMap=map;
    }

    get behaviorConnections() { return this._behaviorConnections; }

    getBehaviorConnection(id) {
        return this._behaviorConnections.get(this._currentBehaviorInstanceId + '-' +id);
    }

    getConnectionsByInstanceId(id){
        return this._behaviorConnections.filter( connection => connection.scriptId === id );
    }

    setBehaviorConnections(map){
        this._behaviorConnections=map;
    }

    // TODO remove ID from the signature. It's in the other param.
    updateBehaviorInstance(id, behaviorInstance) {
        this._behaviorInstanceList = this._behaviorInstanceList.set(id, behaviorInstance);
        return this;
    }

    destroyBehaviorInstance( id ) {
        $(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['delete','behaviors', id, '', this.currentPageId, id ]);

        this._behaviorInstanceList = this._behaviorInstanceList.delete( id );

        // delete behavior commands in each Instances
        const objs = this.getBehaviorInstanceObjects(id);
        objs.map( (cmd, id) => {
            this.destroyBehaviorObject( id );
        });

        return this;
    }

    injectBehaviorInstance( behavior ) {
        this._behaviorInstanceList = this._behaviorInstanceList.set( behavior.id, behavior );
        //this.addBehaviorObject(behavior.id,behavior);
        $(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['add', 'behaviors', behavior.id, behavior.get('text'), this.currentPageId, behavior.id ]);

        return this;
    }

    deleteBehaviorObject(id){
        let completeId = this._currentBehaviorInstanceId + '-' + id;
        this._behaviorObjMap = this._behaviorObjMap.delete( completeId);
        return this;
    }

    // Call this when role edited. Behavior type, notify, may need person change or name change
    syncNotifyBehaviorRoles(){
        // model -> Notifications[] -> {role, roleId, person, personType 1)*
        this._behaviorObjMap
            .filter( commandWidget => commandWidget.model.get('type') === 'notify')
            .map( commandWidget => {

                commandWidget.model.get('notifications')

                    .filter( bn => '1' === bn.personType) // If refs default user from role
                    .map( (bn, index )  => {

                    // Get role by its ID,
                    // update anu Behavior refs setting ref'd role name (called "role") and ref'd person
                    const role = this.getWorkflowRole(bn.roleId);

                    if (role) {
                        // User updated the role name or person
                        bn.role = role.name;
                        bn.person = role.defaultPerson;
                    } else {
                        // User deleted the role, so Role should show Undefined; Role should show < Empty >
                        bn.role = constants.ITEM_UNSPECIFIED;
                        bn.person = constants.ITEM_EMPTY;
                    }

                });
            });
    }

    addBehaviorObject(id, commandWidget, behaviorInstanceId) {
        const completeId = this._currentBehaviorInstanceId + '-' + id;
        commandWidget.behaviorInstanceId = behaviorInstanceId ? behaviorInstanceId : this._currentBehaviorInstanceId;
        this._behaviorObjMap = this._behaviorObjMap.set( completeId, commandWidget );
        return this;
    }

    getBehaviorObject( id ) {
        let completeId = this._currentBehaviorInstanceId + '-' + id;
        return this._behaviorObjMap.get( completeId );
    }

    // TODO remove ID from the signature. It's in the other param.
    updateBehaviorObject( id, commandWidget ) {
        let completeId = this._currentBehaviorInstanceId + '-' + id;
        this._behaviorObjMap = this._behaviorObjMap.set(completeId, commandWidget);
        return this;
    }

    destroyBehaviorObject( id ) {
        let currentId = this._currentBehaviorInstanceId ? this._currentBehaviorInstanceId + '-' : "";
        this._behaviorObjMap = this._behaviorObjMap.delete( currentId + id );

        let deletedConnections = [];
        let connections = this.behaviorConnections;
        connections = connections.filter(c => (c.srcCommand && currentId+c.srcCommand.model.attributes.id === currentId+id
                || c.destCommand && currentId+c.destCommand.model.attributes.id === currentId+id) );
        
        connections.map( (k,v) =>  {
            if (this.fcanvas) {
                const target = _(this.fcanvas.getObjects())
                    .filter(w => w.model && this.checkConnectionId(currentId, w.model.get('id'), v) === v)
                    .first();
                this.fcanvas.remove(target);
            }

            //Also remove connection from source object
            const connSrcObject = this.getCanvasObjectById(k.model.get('srcWidgetModel').id);
            if(connSrcObject && connSrcObject.connection){
                let index = -1;
                for (let i = 0; i < connSrcObject.connection.length; i++) {
                    if(connSrcObject.connection[i].line.model.get("id") === k.id){
                        index = i;
                    }
                }

                if(index > -1){
                    let displayBool = connSrcObject.connection[index].line.model.get("branchBooleanDisplay");
                    if(displayBool){
                        displayBool = util.isTrue(displayBool);

                        if(!displayBool){
                            connSrcObject.model.set("falseConnector", '', {silent: true});
                        }
                        else{
                            connSrcObject.model.set("trueConnector", '', {silent: true});
                        }
                    }
                    connSrcObject.connection.splice(index, 1);                    
                }
            }            
       
            let conn = this.destroyBehaviorConnnection( v );
            deletedConnections.push(conn);
        });

        eventBus.trigger(constants.EV_UPDATE_BEHAVIORS_ONLY);
        return deletedConnections;
    }

    checkConnectionId(scriptId, modelId, connectionId ){
        let id = modelId;
        if(!modelId.includes('-') && connectionId.includes('-')){
            id = scriptId + modelId;
        }
        return id;
    }

    getBehaviorInstanceObjects( behaviorInstanceId , filter) {
        let id = (behaviorInstanceId? behaviorInstanceId: this._currentBehaviorInstanceId) + '-';
        let objects = this._behaviorObjMap.filter( c => id + c.behaviorInstanceId === id+behaviorInstanceId );
        if(filter){
            objects = objects.filter(filter);
        }
        return objects;
    }

    getParentNameOfObject( objectId ) {
        let parent = '';
        if (objectId === 'aForm') {
            parent = '';
        } else {
            let obj = this.getObject(objectId);
            if (obj) {
                let pid = obj.model.get('pageID');
                let page = this.getPage(pid);
                parent = page.model.get('name');
            } else {
                parent = 'Form';
            }
        }

        return parent;
    }

    getObjectName( objectId ) {
        let name = '';
        let obj = this.getObject(objectId);
        if(obj){
            name = obj.model.get('name');
        }
        return name;
    }

    getPageBehaviorInstances( pageId , filter, includeForm= false, includeWorkflow=false) {
        let behaviors = this._behaviorInstanceList.filter( c => c.get('pageId') === pageId );
        if (includeForm && pageId==='Form'){
            behaviors = this._behaviorInstanceList.filter( c => c.get('ownerId') === pageId);
        }

         if (includeWorkflow && pageId==='workflow'){
            behaviors = this._behaviorInstanceList.filter( c => c.get('behaviorKey') === pageId);
        }

        if(filter){
            behaviors = behaviors.filter(filter);
        }

        behaviors = behaviors.sort((a,b) => a.get('ownerId').localeCompare(b.get('ownerId')) );
        return behaviors;
    }

    addBehaviorConnection(connectionVO) {
        let id = this._currentBehaviorInstanceId + '-';
        this._behaviorConnections = this._behaviorConnections.set( id+connectionVO.id, connectionVO );
    }

    destroyBehaviorConnnection( id ) {
        return util.destroyBehaviorConnnection(this, id );
    }

    clearBehaviorInstances() {
        // Instances, Commands
        this._behaviorInstanceList = this._behaviorInstanceList.clear();
        this._behaviorObjMap = this._behaviorObjMap.clear();

    }

    resetAllConnections() {
        this._behaviorConnections = this._behaviorConnections.clear();
    }

    //============================================================
    // isDirty means form in server not up-to-date with client
    //============================================================
    get isDirty() { return this._isDirty; }
    set isDirty(bool) { this.setDirty(bool); }

    get isDirtyRecovery() { return this._isDirtyRecovery; }

    resetDirty() { 
        this._isDirty = false; 
        this.setDirtyRecovery(false);
    }

    setDirty(dirty = true) {
        this._isDirty = dirty;
        this.setDirtyRecovery(dirty);
    }

    setDirtyRecovery(dirty = true) {
        if(util.isTrue(this.accountSettings.DesignRecoveryEnabled)){
            this._isDirtyRecovery = dirty;
            if (!dirty){
                if (this.recoverySaveInterval){
                    clearInterval(this.recoverySaveInterval);
                    this.recoverySaveInterval = undefined;
                    console.log('CLEARED recovery save interval on ' + util.toLocalDateTime(new Date(), 'DD MMM YYYY HH:mm:ss'));
                }
            }else if (!this.recoverySaveInterval && this._currentForm){ //if _currentForm has a value means we are in the designer and we should start a new interval
                this.startFormRecoveryInterval();
                console.log('STARTED recovery save interval on ' + util.toLocalDateTime(new Date(), 'DD MMM YYYY HH:mm:ss'));
            }
        }
    }

    startFormRecoveryInterval(){
        const interval = this.accountSettings.DesignRecoveryMinutes * 60000;
        this.recoverySaveInterval = setInterval(()=>{
            if (this.isDirtyRecovery){
                console.log("Saving recovery version");
                this.isRecoverySave = true;
                eventBus.trigger(constants.EV_SAVE_CURRENT_FORM);
            }else {
                console.log('Nothing to save on recovery, starting next interval...' + util.toLocalDateTime(new Date(), 'DD MMM YYYY HH:mm:ss'));
            }
        }, interval);
    }

    //============================================================
    // isMobileOpen is set it true when the mobile wizard is open otherwise false
    //============================================================
    get isMobileOpen() { return this._isMobileOpen; }
    set isMobileOpen(arg) { this._isMobileOpen = arg; }

    //============================================================
    // isMobileDirty is set it true until first change on the wizard
    //============================================================
    get isMobileDirty() { return this._isMobileDirty; }
    set isMobileDirty(arg) { 
        this._isMobileDirty = arg;       
        this.setDirtyRecovery(arg);
    }

    //============================================================
    // mobileCurrentStep set and get the current step for mobile wizard
    //============================================================
    get mobileCurrentStep() { return this._mobileCurrentStep; }
    set mobileCurrentStep(arg) { this._mobileCurrentStep = arg; }

    //============================================================
    // mobileCurrentPage set and get the current step for mobile wizard
    //============================================================
    get mobileCurrentPage() { return this._mobileCurrentPage; }
    set mobileCurrentPage(arg) { this._mobileCurrentPage = arg; }


    //============================================================
    // _mobileWizardCompleted set and get the value for mobile wizard completed
    //============================================================
    get mobileGetWizardCompleted() { return this._mobileWizardCompleted; }
    set mobileSetWizardCompleted(arg) { this._mobileWizardCompleted = arg; }

    //============================================================
    // timesWizardWasOpened set and get the times the Wizard was opened.
    //============================================================

    get timesWizardWasOpened() { return this._timesWizardWasOpened; }
    set timesWizardWasOpened(times) { this._timesWizardWasOpened = times; }

    //============================================================
    // timesWizardCompleted set and get the times the Wizard was Completed.
    //============================================================

    get timesWizardCompleted() { return this._timesWizardCompleted; }
    set timesWizardCompleted(arg) { this._timesWizardCompleted = arg; }


    get mobileControlDragged() { return this._currentObjectDragged; }
    set mobileControlDragged(arg) { this._currentObjectDragged = arg; }

    //============================================================
    // resetCurrentMobileForm set and get the flag to check if the current mobile form should be deleted
    //============================================================
    get resetCurrentMobileForm() { return this._resetCurrentMobileForm; }
    set resetCurrentMobileForm(arg) { this._resetCurrentMobileForm = arg; }


    //Mobile
    addMobilePage( id, pageObj) {
        this._mobilePages = this._mobilePages.set( id, pageObj );
    }

    addMobileSection( id, sectionObj) {
	    if (null === this._mobileSections) {
            this._mobileSections = Immutable.Map();
        }
        this._mobileSections = this._mobileSections.set( id, sectionObj );
    }

    mobileSections() {
        return this._mobileSections;
    }

    mobileSectionsExist(){
        return this._mobileSections && this._mobileSections.size > 0;
    }

    updateMobileSection(id, section) {
        this._mobileSections = this._mobileSections.set(id, section);
    }

    removeSection(id) {
	    if (id) {
            const section = this._mobileSections.get(id);
            if (section) {
                this.removeSectionsFromColumns(section.pageId, section.id);
                this._mobileSections = this._mobileSections.delete(id);
            }
        }
    }

    allSectionsEmpty() {
        return this._mobileObjects.every(control => {
            if (undefined === control.sectionId || control.sectionId.length === 0) {
                return true;
            }
            return false;
        });
    }

    removeAllSections() {
        this._mobileSections = this._mobileSections.clear();
    }

    removeAllObjectsFromSections() {
	    this._mobileObjects.forEach(control => {
            control.sectionId = undefined;
	        this.updateMobileObject(control.id, control)
        })

    }

    addMobileObject( id, ob) {
        this._mobileObjects = this._mobileObjects.set( id, ob );
    }

    deleteMobileObjectbyId(id){
        this._mobileObjects =  this._mobileObjects.delete(id);
        return this._mobileObjects;
    }

    deleteMobileNewObjectbyId(id){
        this._mobileNewObjects = this._mobileNewObjects.delete(id);
        return this._mobileNewObjects;
    }


    addMobileObjectNew( id, ob) {
        this._mobileNewObjects = this._mobileNewObjects.set( id, ob );
    }

    getMobileNewObjectById(id) {
        return this.mobileNewObjects.get( id );
    }

    updateMobileObject( id, ob) { 
        this._mobileObjects = this._mobileObjects.set( id, ob );

        const mobileControl = mobileListService.getMobileControl(id);
        if (mobileControl) {
            // Font styles
            mobileControl.model.set('fontSize', ob.attributes.fontSize, { silent: true });
            if (ob.attributes.fontWeight) {
                mobileControl.model.set('fontWeight', ob.attributes.fontWeight, { silent: true });
            }
            if (ob.attributes.fontStyle) {
                mobileControl.model.set('fontStyle', ob.attributes.fontStyle, { silent: true });
            }
            
            mobileControl.model.set('height', ob.height, { silent: true });

            if (ob.margins) {
                mobileControl.model.set('margins', ob.margins, { silent: true });

            }
            if (ob.attributes && ob.attributes.marginSize) {
                mobileControl.model.set('marginSize', ob.attributes.marginSize, { silent: true });
            }
        }

    }

    getMobileObject(id) {
        return this.mobileObjects.get( id );
    }

    get mobileObjects() {
        return this._mobileObjects;
    }

    get mobileNewObjects() {
        return this._mobileNewObjects;
    }

    removeMobileNewObjects() {
        this._mobileNewObjects = this._mobileNewObjects.clear();
    }

    controlHasSection(id) {
        let controlFound = this.mobileObjects.find(control => control.id === id && control.sectionId);

        return controlFound !== undefined; 
    }


    mobileNewObjects() {
        return this._mobileNewObjects;
    }

    mobileObjectsByPageId(pageId) {
        return Immutable.Map(this._mobileObjects.filter( s => s.pageId === pageId ));
    }

    mobileNewObjectsByPageId(pageId) {
        return Immutable.Map(this._mobileNewObjects.filter( s => s.pageId === pageId ));
    }

    removeAllNewObjectsByPageId(pageId) {
        return this._mobileNewObjects = this._mobileNewObjects.filter( object => object.pageId === pageId ).clear();
    }

    countMobileObjectsBySectionId(sectionId) {
        return this._mobileObjects.filter( s => s.sectionId === sectionId ).size;
    } 

    getSectionById(sectionId) {
        return this._mobileSections.filter( s => s.id === sectionId );
    }

    getSection(id){    
        return this._mobileSections.get( id );
    }

    getSectionsByPageId(pageId) {
        let sortItems = (section1, section2) => {
            if (section1.index < section2.index) {
                return -1;
            }

            if (section1.index > section2.index) {
                return 1;
            }

            return 0;
        };

        let filter = section => section.pageId === pageId; 
        let sections = this.mobileSections();

        if (sections) {
            return sections
                .filter(filter)
                .sort(sortItems);
        }

        return null;
    }

    getMobileObjectsBySectionId(sectionId) {
        return this._mobileObjects.filter( s => s.sectionId === sectionId );
    }

    removeMobileObjectById(objectId) {
        let control = this.getMobileObject(objectId);
        control.sectionId = undefined;
    } 

    get mobileColumns() {
        return this._mobileColumns;
    }

    addMobileColumn( id, obj) {
        this._mobileColumns = this._mobileColumns.set( id, obj );
	}

    createColumn(name, order, pageId) {
        let column = {
            id: sequenceService.nextDomId(),
            name: name,
            pageId: pageId,
            sections: Immutable.Map(),
            order: order                
        };

        this.addMobileColumn(column.id, column);
    }

    getColumn(pageId, order) {
        return this._mobileColumns.find(column => column.pageId === pageId && column.order === order);
    }

    getColumnsByPageId(pageId) {
        return this._mobileColumns.filter(c => c.pageId === pageId);
    }

    updateColumn(id, columnObj) {
        this._mobileColumns = this._mobileColumns.set( id, columnObj );
    }

    existSecondColumnConfiguration(){
       return this._mobileColumns.filter( c => c.name === 'Column 2').size >= 1;
    }

    existSecondColumnConfigurationByPage(pageId){
        return this._mobileColumns.filter( c => c.name === 'Column 2' && c.pageId === pageId).size >= 1;
    }

    addSectionsToColumn(pageId, columnOrder) {
        let column = this.getColumn(pageId, columnOrder);
        let index = 1;

        let sections = this.getSectionsByPageId(pageId);

        if (sections) {
            sections = sections
                .filter(section => this.getMobileObjectsBySectionId(section.id).size > 0)
                .map((section) => {
                    return { id: section.id, order: index++ }   
            });
   
            column.sections = sections;
        
            this.updateColumn(column.id, column);
        }
    }

    addMissingSections(pageId, firstOrder = 1, secondOrder = 2) {
        const mapColumns = column =>  {
                let sections = (column.sections) ? column.sections.map(section => section.id).toArray() : [];
                return { order: column.order, 
                         sections: sections
            };
        };

        let columnSections = this.getColumnsByPageId(pageId)
                                 .filter(column => column.order > 0)
                                 .map(mapColumns)
                                 .toArray();

        let column1 = columnSections.find(column => column.order === firstOrder);
        let column2 = columnSections.find(column => column.order === secondOrder);

        const getMaxOrder = (sections) => {
            return  Math.max(...sections.map(section => section.order).toArray()) + 1;
        };

        const filterSections = section => !column1.includes(section.id) && !column2.includes(section.id) && this.getMobileObjectsBySectionId(section.id).size > 0;

        column1 = column1 ? column1.sections : [];
        column2 = column2 ? column2.sections : [];

        let sections = this.getSectionsByPageId(pageId); 

        if (sections) {
            let firstColumn = this.getColumn(pageId, firstOrder);

            sections.filter(filterSections)
                .forEach(section => {
                    firstColumn.sections = firstColumn.sections.set(section.id, {id: section.id,
                        order: getMaxOrder(firstColumn.sections)
                    });
                });

            this.updateColumn(firstColumn.id, firstColumn,);
        }           
    }

    resetColumnsStepThree(pageId, firstOrder = 1, secondOrder = 2) {
        this.deleteMobileColumnsByOrder(pageId, firstOrder);
        this.deleteMobileColumnsByOrder(pageId, secondOrder);
        this.createColumn('First Column', firstOrder, pageId);
        this.addSectionsToColumn(pageId, firstOrder);
    }

    removeSectionsWhenRemovingControls(pageId, sectionId, firstOrder = 1, secondOrder = 2) {
        if (this.countMobileObjectsBySectionId(sectionId) === 0) {
            this.removeSectionsFromColumns(pageId, sectionId, firstOrder, secondOrder);
        }        
    }
    
    removeSectionsFromColumns(pageId, sectionId, firstOrder = 1, secondOrder = 2) {
        const removeSection = (pageId, orderColumn) => {
            let column = this.getColumn(pageId, orderColumn, sectionId);
            if (column && column.sections) {
                column.sections = column.sections.delete(sectionId);
                this.updateColumn(column.id, column);
            }
        };

        removeSection(pageId, firstOrder, sectionId);
        removeSection(pageId, secondOrder, sectionId);
    }

    initializeSectionsColumn(pageId, columnOrder) {
        let column = this.getColumn(pageId, columnOrder);

        column.sections = new Immutable.Map();
        this.updateColumn(column.id, column);
    }

    getSectionFromColumn(pageId, columnOrder, sectionId) {
        const column = this.getColumn(pageId, columnOrder);

        if(column && column.sections && column.sections.size){
            return column.sections.filter(section => section.id === sectionId);
        }
        return null;
    }

    resetMobileObjects(){
        if (null !== this._mobilePages) { this._mobilePages = this._mobilePages.clear(); }
        if (null !== this._mobileObjects) { this._mobileObjects = this._mobileObjects.clear(); }
        if (null !== this._mobileColumns) { this._mobileColumns = this._mobileColumns.clear(); }
        this._isMobileOpen = false;
        this._isMobileDirty = false;
        this._timesWizardCompleted = 0;
        if (null !== this._mobileSections) { this._mobileSections = this._mobileSections.clear(); }
    }

    resetMobileColumns() {
        if (null !== this._mobileColumns) { this._mobileColumns = this._mobileColumns.clear(); }
    }

    initMobileSections() {
        this._mobileSections = null;
    }

    deleteMobilePages(pageId) {
	    this._mobilePages = this._mobilePages.delete(pageId);
    }

    deleteMobileColumns() {
        this.pageList.forEach(page => {
            let columnDefault = this.getColumn(page.id, mobileConstants.CURRENT_COLUMN_ORDER);

                this._mobileColumns = this._mobileColumns.filter(column => (column.order !== mobileConstants.FIRST_COLUMN_ORDER
                                                                         && column.order !== mobileConstants.SECOND_COLUMN_ORDER)
                                                                         && column.pageId === page.id);
            this.getSectionsByPageId(page.id).every(item => {
                let section = item;
                section.columnId = (columnDefault) ? columnDefault.id : '';
                this._mobileSections = this._mobileSections.set(section.id, section);
            });
        });
    }

    deleteMobileColumnsByPage(pageId) {
        this._mobileColumns = this._mobileColumns.filter( s => s.pageId !== pageId );
    }

    deleteMobileColumnsByOrder(pageId, columnOrder) {
        const column = this.getColumn(pageId, columnOrder);
        if(column){
            this._mobileColumns = this._mobileColumns.filter( item => item.id !== column.id );
        }
     }

    deleteMobileSectionsByPage(pageId) {
        if(null !== this._mobileSections){
            this._mobileSections = this._mobileSections.filter( s => s.pageId !== pageId );
        }
    }

    deleteMobileObjectsByPage(pageId) {
        this._mobileObjects = this._mobileObjects.filter( s => s.pageId !== pageId );
    }

    addControlHtmlSections(id, control) {
        this._controlHtmlMobileSections = this._controlHtmlMobileSections.set(id, control);
    }

    getControlHtmlSections(controlId) {
        return this._controlHtmlMobileSections.get(controlId);
    }

    updateDefaultColumnLayout() {
	    this.pageList.forEach(page => {
            const sectionIds = this.getSectionsByPageId(page.id).map(item => {return {id: item.id, order: item.index}});
            let defaultColumn = this.getColumn(page.id, mobileConstants.CURRENT_COLUMN_ORDER);
            if (!defaultColumn) {
                this.createColumn(mobileConstants.CURRENT_COLUMN_NAME,
                    mobileConstants.CURRENT_COLUMN_ORDER,
                    page.id);
                defaultColumn = this.getColumn(page.id, mobileConstants.CURRENT_COLUMN_ORDER);
            }
            defaultColumn.sections = sectionIds;
            this.updateColumn(defaultColumn.id, defaultColumn);
        });
    }

    /*****
     * For the wizard
    */

    isMobileCurrentStepOne() {
        return this.mobileCurrentStep === 1;
    }

    isMobileCurrentStepTwo() {
        return this.mobileCurrentStep === 2;
    }

    isMobileCurrentStepThree() {
        return this.mobileCurrentStep === 3;
    }
    
    isMobileCurrentStepFour() {
        return this.mobileCurrentStep === 4;
    }

    isMobileCurrentStepFour() {
        return this.mobileCurrentStep === 4;
    }

    //============================================================
    // Current form BB view
    // Form base is invariant across desktop views. It reflects
    // the four attributes of the XML <form> element.
    //============================================================
    get currentForm() { return this._currentForm; }
    set currentForm(aForm) { this._currentForm = aForm; }

    get formBase() { return this._formBase; }
    resetFormBase() { this._formBase = {}; }

    //============================================================
    // Current kind of designer showing. E.g. 'L','B','W', or 'R'
    //============================================================

    get mainKind()          { return this._mainKind; }
    set mainKind( kind )    { this._mainKind = kind; }

    //============================================================
    // Current type root document. Can be form or report.
    //============================================================
    get docType()           { return this._docType; }

    setDocTypeForm()    { this._docType = constants.DOC_TYPE_FORM; }
    setDocTypeReport()  { this._docType = constants.DOC_TYPE_REPORT; }

    resetDocType()      { this.setDocTypeForm(); }

    
    //============================================================
    // Custom Code Blocks section ....

    get codeBlocks() { return this._codeBlocks; }
    set codeBlocks( blocks ) { this._codeBlocks = blocks; }

    getAllCodeBlocks() {
        return JSON.parse(JSON.stringify(this._codeBlocks));
    }

    setAllCodeBlocks(blocks) {
        const newBlocks = JSON.parse(JSON.stringify(blocks));
        this._codeBlocks = newBlocks.sort((a, b) => a.order - b.order);
    }

    clearCustomCode(){
        this.codeBlocks = [];
    }
    

    //============================================================
    // Multi-language section ....

    get isMultilanguageDirty() { return this._isMultilanguageDirty; }
    set isMultilanguageDirty(bool) { this._isMultilanguageDirty = bool; }

    get multilanguage() { return this._multilanguage; }
    set multilanguage(json) { this._multilanguage = json;}

    get currentLanguage() { return this._currentLanguage; }
    set currentLanguage(lang) { this._currentLanguage = lang;}    

    getAllLanguages() {
        return this._multilanguage.languages || [];
    }

    saveLanguages(array) {
        this._multilanguage.languages = [...array];
    }

    getAllTranslations() {
        return { ...this._multilanguage.translations };
    }

    getTranslationObjectById(id) {

        const translations = this._multilanguage.translations;

        if (translations && translations[id]) {
            return translations[id];
        }
        for (const objectKey in translations) {
            const currentObject = translations[objectKey];

            if (currentObject && typeof currentObject.columns === 'object' && currentObject.columns !== null) {
                if (currentObject.columns[id]) {
                    return currentObject.columns[id];
                }
            }
        }
        return null;
    }

    saveTranslations(translationsObj) {
        this._multilanguage.translations = { ...translationsObj };
    }

    clearMultilanguage(){
        this.multilanguage = {
            languages: [],
            translations:  {}
        };
        this.currentLanguage = 'default';
        this.isMultilanguageDirty = false;
    }


    //============================================================
    // Advanced CSS section ....

    get isAdvancedCSSDirty() { return this._isAdvancedCSSDirty; }
    set isAdvancedCSSDirty(bool) { this._isAdvancedCSSDirty= bool; }

    get isAdvancedCSSEnabled() { return this._isAdvancedCSSEnabled; }
    set isAdvancedCSSEnabled(bool) { this._isAdvancedCSSEnabled= bool; }    

    get advancedCSS() { return this._advancedCSS; }
    set advancedCSS(json) { this._advancedCSS = json;}

    clearAdvancedCSS(){
        this.advancedCSS = {};
        this.isAdvancedCSSDirty = false;
        this.isAdvancedCSSEnabled = false;
    }


    //============================================================
    // Workflow section ....

    addWorkflowObject( workflowObject, opt={} ){
        switch(workflowObject.options.type){
            case 'workflow_comment':
                this.addWorkflowComment(workflowObject, opt);
                break;
            case 'workflow_stage':
                this.addWorkflowStage(workflowObject, opt);
                break;
            case 'path':
                break;
            case 'role':
                this.addWorkflowRole(workflowObject, opt);
                break;
            default:
                throw "Tried to add an invalid object to workflow " + JSON.stringify(control);
        }
    }

    xxx_deleteWorkflowObject(workflowObject, opt={}){
        switch(workflowObject.options.type){
            case 'workflow_comment':
                this.deleteWorkflowComment(workflowObject, opt);
                break;
            case 'workflow_stage':
                this.deleteWorkflowStage(workflowObject, opt);
                break;
            case 'path':
                break;
            case 'role':
                this.deleteWorkflowRole(workflowObject, opt);
                break;
            default:
                throw "Tried to delete an invalid object to workflow " + JSON.stringify(control);
        }
    }


    getWorkflowStages(useDefault, addAllOption){
        let returnStages = this._workflow.stages;
        const newModel = new Model({name: 'All', id:'all'});

        if(this._workflow.stages.size === 0 && useDefault){
            returnStages = this._workflow.stages.set('all', {model:newModel, id:'all'});
        } else if(this._workflow.stages.size > 0 && addAllOption){

            returnStages = Immutable.OrderedMap();
            returnStages = returnStages.set('all', {model:newModel, id:'all'});

            this._workflow.stages.map(function (val, key) {
                returnStages = returnStages.set(key, val);
            });
        }

        return returnStages;
    }

    getWorkflowStageByID(id) {
        let result = null;
        if(this._workflow && this._workflow.stages.size > 0 ) {
            result = this._workflow.stages.find(function(obj){
                return obj.model.get('id') === id ?  obj: null;});
        }
        return result;
    }

    _suffix( opt ){
        return opt.noAutoSuffix
            ? constants.STRING_EMPTY
            : ` ${this._workflow.stages.size + 1}`;
    }

    addWorkflowStage( workflowObject, opt={} ) {
        //deleting circular references
        delete workflowObject.$el;
        delete workflowObject._listeningTo;
        delete workflowObject.options.el;
        this._workflow.stages = this._workflow.stages.set(workflowObject.id, workflowObject);
        return workflowObject;
    }
    xxx_deleteWorkflowStage(workflowObject){
        this._workflow.stages = this._workflow.stages.delete(workflowObject.id);



    }


    addWorkflowComment( workflowObject, opt={} ) {
        workflowObject.model.set('name', workflowObject.model.get('name') + this._suffix( opt ), {silent:true});
        this._workflow.comments = this._workflow.comments.set(workflowObject.id, workflowObject);
        return workflowObject;
    }
    deleteWorkflowComment(workflowObject){
        this._workflow.comments = this._workflow.comments.delete(workflowObject.id);
    }

    getWorkflowStageOrComment( id ) {
        let obj = this._workflow.stages.get( id );
        if (!obj) {
            obj = this._workflow.comments.get( id );
        }

        return obj;
    }

    addWorkflowPath( connectionVO ) {
        this._workflow.paths = this._workflow.paths.set( connectionVO.id, connectionVO );
        return this._workflow.paths;

    }
    deleteWorkflowPath(workflowObject){
        const conn = this._workflow.paths.get(workflowObject.id);
        this._workflow.paths = this._workflow.paths.delete(workflowObject.id);
        return conn; // so we can track them in UNDO
    }

    getWorkflowPath( id ) {
        return this._workflow.paths.get( id );
    }

    addWorkflowRole( workflowObject ) {

        undoService.add({
            undo: () =>{
                this._workflow.roles = this._workflow.roles.delete(workflowObject.id);
                eventBus.trigger(constants.EV_ROLE_OR_STAGE_UPDATED)
            },
            redo: () =>{
                this._workflow.roles = this._workflow.roles.set(workflowObject.id, workflowObject);
                eventBus.trigger(constants.EV_ROLE_OR_STAGE_UPDATED)
            }
        }, textConstants.UNDO_NEW_ROLE);

        this._workflow.roles = this._workflow.roles.set(workflowObject.id, workflowObject);
        return workflowObject;
    }
    deleteWorkflowRole(workflowObject){
        undoService.add({
            undo(){
                this._workflow.roles = this._workflow.roles.set(workflowObject.id, workflowObject);
                this.syncNotifyBehaviorRoles();
            },
            redo(){
                this._workflow.roles = this._workflow.roles.delete(workflowObject.id);
                this.syncNotifyBehaviorRoles();
            }
        }, textConstants.UNDO_DELETE_ROLE);

        this._workflow.roles = this._workflow.roles.delete(workflowObject.id);
        this.syncNotifyBehaviorRoles();
    }

    getWorkflowRoles( useDefault, addAllOption ){
        let returnRoles = this._workflow.roles;

        if(this._workflow.roles.size === 0 && useDefault){
            returnRoles = this._workflow.roles.set('all', {name:'All', id:'all'});
        } else if(this._workflow.roles.size > 0 && addAllOption){

            // returnRoles = Immutable.OrderedMap();
            // returnRoles = returnRoles.set('all', {name:'ALL', id:'all'});

            returnRoles = returnRoles.clear().set('all', {name:'All', id:'all'});

            this._workflow.roles.map( (val, key) => returnRoles = returnRoles.set(key, val) );
        }

        return returnRoles;
    }

    getWorkflowComments(){
        return this._workflow.comments;
    }

    getWorkflowRole( id ) {
        return this._workflow.roles.get( id );
    }

    getWorkflowStage( id ) {
        return this._workflow.stages.get( id );
    }

    getWorkflowDefaultStage(){
        let result = null;
        if(this._workflow && this._workflow.stages.size > 0 ) {
            result = this._workflow.stages.find(function(obj){
                return util.isTrue(obj.model.get('isDefault'));
            });
        }
        return result;
    }

    getWorkflowInitialStage(){
        let currentStage = this.getCurrentStage();
        let defaultStage = this.getWorkflowDefaultStage();

        return currentStage.toLowerCase() === "all" && defaultStage ? defaultStage.id : currentStage;
    }

    getworkflowComment( id ) {
        return this._workflow.comments.get( id );
    }

    setWorkflowProperties( props ) {
        this._workflow.properties = Immutable.Map( props );
    }

    get workflowProperties() { return this._workflow.properties; }
    get workflowComments()   { return this._workflow.comments; }
    get workflowStages()     { return this._workflow.stages; }
    get workflowPaths()      { return this._workflow.paths; }
    get workflowRoles()      { return this._workflow.roles; } // Unfiltered

    clearWorkflow() {
        this._workflow = {
            properies:  Immutable.Map(),
            comments:   Immutable.Map(),
            stages:     Immutable.OrderedMap(),
            paths:      Immutable.Map(),
            roles:      Immutable.OrderedMap()
        };
    }

    deleteWorkflowObject(workflowObject) {
        let deletedConnections = [];
        switch(workflowObject.model.get('factoryKey')) {
            case 'workflow_stage':
                deletedConnections = this.deleteWorkflowStage(workflowObject);
                break;
            case 'workflow_comment':
                this.deleteWorkflowComment(workflowObject);
                break;
            case constants.CONNECTION_FACTORY_KEY:
                break;

        }

        return deletedConnections;
    }

    deleteWorkflowStage(workflowObject) {
        let id = workflowObject.model.get('id');
        let isDefault = workflowObject.model.get('isDefault');
        this._workflow.stages = this._workflow.stages.delete( workflowObject.model.get('id'));
        let stage = this.getWorkflowStages(false,false).first();

        if (isDefault === true && stage){             
             stage.model.set('isDefault',true);
             this.workflowProperties._root.entries[2][1] = stage.model.get('id');
        }
        
        let deletedConnections = [];
        let connections = this.workflowPaths;
        connections = connections.filter(c => (c.srcCommand && c.srcCommand.model.attributes.id === id || c.destCommand && c.destCommand.model.attributes.id === id) );

        connections.map( (v, k) =>  {
            const target = _( this.fcanvas.getObjects() )
                .filter( w => w.model && w.model.get('id') === k )
                .first();
            this.fcanvas.remove( target );
            this.deleteWorkflowPath( v );
            deletedConnections.push( v );
        });

        return deletedConnections;
    }

    deleteWorkflowComment(workflowObject) {
        this._workflow.comments = this._workflow.comments.delete( workflowObject.model.get('id') );
    }

    // ... end of workflow sectinonl
    //========================================================

    //========================================================
    // Objects (controls, pages, forms, non-layout controls
    //========================================================
	addObject( id, control, options={} ) {
        // PF-2223: When pasting, the element should be pasted on the correct page, not on the active page
        control.pageId = options.pageId || this._currentPageId;
        control.model.set('pageId', control.pageId, { silent: true });
        control.model.set('pageID', control.pageId, { silent: true }); //TODO - pageID must be deprecated in favor of pageId

		this._objMap = this._objMap.set( id, control );

        if (!options.silent) {
            var name = control.model.get('name');

            window.setTimeout(() => {
                $(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['add', 'object', id, name, control.pageId, control._groupId ]);
            }, 100);
            //$(document).trigger(constants.EV_PAGES_UPDATED);
            //eventBus.trigger(constants.EV_PAGE_OBJECTS_UPDATED, 'add', 'object', id, name, control.pageId );
        }


        return this;
	}

	getObject( id ) {
 		return this._objMap.get( id );
	}

    getObjectByParam( param, value, caseSensitive ) {
        let control=null;
        let lstControls=this._objMap.toArray();
        let size=lstControls.length;

        for(let i=0;i<size;i++){
            let _controlName = lstControls[i].model.get(param).trim();
            let _value = value.trim();
            if (!caseSensitive){
                _controlName = _controlName.toLowerCase();
                _value = _value.toLowerCase();
            }
            
            if(_controlName === _value){
                control = lstControls[i];
                break;
            }
        }
        return control;
    }

    getObjectByFactoryKey(key){
        return this._objMap.filter(obj => obj.model.get('factoryKey') === key);
    }

    updateObject( id, control, options={} ) {
        this._objMap = this._objMap.set(id, control);
        if (!options.silent) {
            //$(document).trigger(constants.EV_PAGES_UPDATED);
            eventBus.trigger(constants.EV_PAGE_OBJECTS_UPDATED, options);
        }
        return this;
    }

	clearObjectList( options={} ) {
		this._objMap = this._objMap.clear();

        if ( !options.silent ) {
            //$(document).trigger(constants.EV_PAGES_UPDATED);
            eventBus.trigger(constants.EV_PAGES_UPDATED);
        }
        return this;
	}

    destroyObject( id, options={} ) {
        
        utilMobile.deleteElementIntoMobileObject(id);
        
        const _control = this.getObject(id);
        const pageId = _control ? _control.model.get('pageId') : this.currentPageId;
        this._objMap = this._objMap.delete( id );
        this.clearSelected();


        if ( !options.silent ) {
            //$(document).trigger(constants.EV_PAGES_UPDATED);
            //eventBus.trigger(constants.EV_PAGES_UPDATED);
            $(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['delete', 'object', id, '', pageId ]);
        }
        
        return this;
    }

    injectGroupChildren(model) {
        // TODO - PF-1871
        if (model.get('childList')) {
            Immutable.List(model.get('childList')).map(child => {
                const _fp = factoryService.factories[child.get('factoryKey')];
                const vw = _fp.factory.createView(child.get('factoryKey'), child.get('pos'));

                for (var p in child.attributes) {
                    if ( child.attributes.hasOwnProperty(p) && (child.attributes[p] !== undefined)) {
                        vw.model.attributes[p] = child.attributes[p];
                    }
                }
                this.injectGroupChildren(vw.model);
                this.addObject(vw.id, vw);
            });
        }

    }

    injectObject( object, options={}){
        this._objMap = this._objMap.set( object.id, object );

        // need to process GroupChildren
        this.injectGroupChildren(object.model);


        if ( !options.silent ) {
            //$(document).trigger(constants.EV_PAGES_UPDATED);
            eventBus.trigger(constants.EV_PAGES_UPDATED);
        }
        return this;
    }

    // i.e. Return map containing only controls on the given page.
    getPageControls( pageId , filter) {
        let controls = this._objMap.filter( c => c.pageId === pageId );
        if(filter){
            controls = controls.filter(filter);
        }
        return controls;
    }

    getTableInfo(page, columnId){
        let self = this;
        let tables = this._objMap.filter(obj=> obj.model.get('factoryKey') === 'control_table')
                        .filter(table=> table.model.get('pageId')=== page );
        let result = null;
        tables.forEach(t=>{
            let selectedColumn = t.model.get('gridData').filter(col=> self.cleanId(col.get('id'))=== columnId);
            if (selectedColumn && selectedColumn.length === 1) {
                result = {tableInfo: t, selectedColumn: selectedColumn};
            }
        });
        return result;
    }

    getAllControlsByPage( filter ) {
        const pages = [];
        this.pageList.map( (v, k) =>{
                pages.push({
                    id: v.model.get('id'),
                    name: v.model.get('name'),
                    number: v.model.get('pageNumber'),
                    controls: this.getPageControls(v.model.get('id'), filter)

                })

            }
        );
        return pages;
    }

    getAllPagesByControl( filter ) {
        const pages = [];
        this.pageList.map( (v, k) =>{
                let controls = this.getPageControls(v.model.get('id'), filter);
                if (controls.size > 0){
                   pages.push({
                       id: v.model.get('id'),
                       name: v.model.get('name'),
                       number: v.model.get('pageNumber'),
                       controls: controls
                   })
               }
            }
        );
        return pages;
    }

    getFilteredControls( filter ){
        return this._objMap.filter(filter);
    }

    getMultiSelectValues(v){
        let selectFields = [];
        if(v.model.get('gridData') && v.model.get('gridData').length){
            v.model.get('gridData').forEach((data, index) => {
                selectFields.push( {id: v.id + '$' + index, text: data.item || data.caption } );
            });
        }
        return selectFields;
    }

    // Generates a simple array of objects to be used in displaying Page-Controls for selection
    getAllControlsByPageList(filter, addValueAndCaption, mode, factoryKeyToFilter=null) {
        var self = this;
        let pages = [];
        this.pageList.map( (v, k) =>{
                pages.push({
                    id: v.model.get('id'),
                    name: v.model.get('name'),
                    number: v.model.get('pageNumber')
                })
            }
        );

        var pageControls = Immutable.Map();
        var controls = [];
        var childControls = [];

        const getSelectFields = (parent, childControls) => {

            let factoryKey = "";
            _.forEach(childControls, function(cc) {

                if (cc.get('childList')) {
                     getSelectFields(cc, cc.get('childList'));
                }

                let object = self.getObject(cc.id);

                factoryKey = cc.get('factoryKey');
                if ((factoryKey === 'control_radio_button' || factoryKey === 'control_drop_down' || (parent.get('factoryKey') === 'control_list' && !util.isTrue(parent.get('multiSelection'))) && addValueAndCaption)) {
                    parent.set('selectFields',[{id: parent.get('id') + '$v' ,text: 'Value'}, {id: parent.get('id'), text: 'Caption'}], {SILENT:true});
                }
                else if(factoryKey === 'control_ranking' || (factoryKey === 'control_list' && util.isTrue(object.model.get('multiSelection')))) {
                    cc.selectFields = self.getMultiSelectValues(object);
                }
            });
           
        };

        _.forEach(pages , function(p) {
            pageControls = self.getPageControls(p.id, filter);
            controls = [];
            p.selectFields = [];

            pageControls.forEach( (v,k) => {
                childControls = undefined;
                const factoryName = factoryService.factories[v.model.get('factoryKey')].name;

                // if any of the immediate children are Radio Buttons, then add the [Value] ad [Caption] selectFields
                if (v.model.get('factoryKey') === 'control_radio_button' && addValueAndCaption) {
                    p.selectFields = [{id: p.id + '$v', text: 'Value'}, {id: p.id, text: 'Caption'}];
                }
                
                if (v.model.get('factoryKey') === 'control_table') {
                    tableService.buildSelectList(v.model);
                }

                 //PF-6504 Refactor to get all the selectFields when there is a radio button, list, dropdown and ranking in a group. It should consider all the levels.
                 getSelectFields(v.model, v.model.get('childList'));

                 if (v.model.get('childList')) {
                     // PF-4548: Filter out children list to only valid ones, according to our validControl list
                     childControls = self.getControlChildren(v.model.get('childList')).filter(child=>self.validControl(child.factoryKey));
                 }
 

                if ((v.model.get('factoryKey') === 'control_drop_down' || (v.model.get('factoryKey') === 'control_list' && !util.isTrue(v.model.get('multiSelection')))) && addValueAndCaption) {
                    v.model.set('selectFields',[{id: v.id + '$v', text: 'Value'}, {id: v.id, text: 'Caption'}], {SILENT:true});
                }
                if(v.model.get('factoryKey') === 'control_ranking' || (v.model.get('factoryKey') === 'control_list' && util.isTrue(v.model.get('multiSelection')))) {
                   v.model.set('selectFields', self.getMultiSelectValues(v), {SILENT:true});
                }

              
                let flag = ((mode && mode === 'objects'  && !self.validControl(v.model.get('factoryKey'))) ? false: true);

                if(flag){
                    controls.push({
                        id: v.model.get('id'),
                        factoryKey: v.model.get('factoryKey'),
                        icon: v.model.get('icon'),
                        name: v.model.get('name'),
                        selectFields: v.model.get("selectFields"),
                        controls: childControls,
                        isNested: !!v.groupId,
                        groupId:v.groupId ? v.groupId : null,
                        tooltip:`${factoryName} - ${v.model.get('name')}`
                    })
                }

            });

            p.controls = controls;
        });
        return pages;
    }

    getChildrenFromGroup(control){
	    let childControls=[];
	    let currentNode=control;
	    while(currentNode.model.get('traceGroup')){
	        let nextControl=this.getObject(currentNode.model.get('traceGroup'));
            const factoryName = factoryService.factories[nextControl.model.get('factoryKey')].name;
            childControls.push({
                id:nextControl.model.get('id'),
                factoryKey:nextControl.model.get('factoryKey'),
                icon:nextControl.model.get('icon'),
                name:nextControl.model.get('name'),
                selectFields:null,
                nextControl:nextControl.model.get('traceGroup') || null,
                groupId:nextControl.groupId,
                tooltip:`${factoryName} - ${nextControl.model.get('name')}`
            });
            currentNode=nextControl;
        }
        return childControls
    }

    getRootNode(control){
	    if(!control.groupId){
	        return control;
        }
	    let parentGroup = this.getObject(control.groupId);
	    parentGroup.model.set('traceGroup',control.model.get('id'));
        return this.getRootNode(parentGroup)
    }

    validControl(factoryKey){
        const validControls={
            'control_multiple_text_input':true,
            'control_text_input':true,
            'control_numeric_input':true,
            'control_date_input':true,
            'control_time_input':true,
            'control_date_calendar':true,
            'control_checkbox_group':true,
            'control_radio_button_group': true,
            'control_checkbox':true,
            'control_radio_button':true,
            'control_drop_down':true,
            'control_mask_text_input':true,
            'control_numeric_stepper':true,
            'control_password_input':true,
            'control_vertical_slider':true,
            'control_horizontal_slider':true,
            'control_table':true,
            'control_ranking':true,
            'control_file_attachment':true,
            'control_group':true,
            'control_button':true,
            'control_picture':true,
            'control_text':true,
            'control_small_text':true,
            'control_large_heading':true,
            'control_medium_heading':true,
            'control_small_heading':true,
            'control_bullet_text':true,
            'control_list':true,
            'control_vertical_separator':true,
            'control_horizontal_separator':true,
            'control_link':true,
            'control_progress_bar':true,
            'control_print_button':true,
            'control_submit_button':true,
            'control_play_form_button':true,
            'control_play_report_button':true,
            'control_page_button':true,
            'control_websignature':true
        };
        return validControls.hasOwnProperty(factoryKey);
    }


    // Used in conjunction with getAllControlsByPageList for building simple select list
    getControlChildren(controlList) {
        var _childControls = undefined;
        var _children = [];

        if (controlList) {
            controlList.map((v, k) => {
                _childControls=undefined;
                if (v.get('childList')) {
                    _childControls = this.getControlChildren(v.get('childList'));
                }

                var selectFields = v.get('selectFields');
                _children.push({
                    id: v.get('id'),
                    factoryKey: v.get('factoryKey'),
                    icon: v.get('icon'),
                    name: v.get('name'),
                    selectFields: (selectFields) ? selectFields : undefined,
                    controls: _childControls
                });
            });
        }


        return _children;
    }


    getBehaviorsWithUpdatedStatus(objectId,factoryKey){
        let availableBehaviors=objectBehaviorMap.getBehaviors(factoryKey);
        if (availableBehaviors.length > 0) {

            _.forEach(availableBehaviors, ((item) => {
                var instance = this.getBehaviorInstanceByObjectAndBehaviorKey(objectId, item.behaviorKey);
                if (instance) {
                    item.active = 'active';
                } else {
                    item.active = '';
                }

            }));
        }
        else {
            availableBehaviors = undefined;
        }
        return availableBehaviors;
    }

    getActiveBehaviors(element) {
        let objectId = element[0].id === 'aPageSel' ? 'aForm' : element[0].id;
        let factoryKey = this.getObject(objectId).model.get('factoryKey');
        let currentBehaviors = this.getBehaviorsWithUpdatedStatus(objectId, factoryKey);
        console.log('Current Behaviors ', currentBehaviors);
        let activeBehaviors = currentBehaviors.filter((item)=> {
            return item.active !== '';
        });
        console.log('Active Behaviors', activeBehaviors);
        return activeBehaviors;
    }

    addDataSource( datasource ) {
        this._dataSources = this._dataSources.set( datasource.id, datasource);
    }

    countDataSourcesUses(idDataSource){
        let result=this._objectMap.filter( (v, k) => v.dataSourceId === idDataSource);
        return result;
    }

    getDataSources(id) {
        let ds;
        if (id) {
            ds = this._dataSources.get(id);
        } else {
            ds = this._dataSources;
        }

        return ds;
    }

    updateDataSource( datasource ) {
        this._dataSources = this._dataSources.set( datasource.id, datasource);
    }

    deleteDataSource( id ) {
        const currDS = this._dataSources.get(id);
        const formDependency = {id: currDS.formGUID};
        let dependencyExists = false;

        this._dataSources = this._dataSources.delete( id );

        this._behaviorObjMap
            .filter(command => command.model.get('dataSourceId') === id)
            .forEach(command => command.model.set('dataSourceId', undefined, {silent: true}));

        // check if the form dependency exists in another DS
        this._dataSources.map((v,k) => {
            if (v.formGUID === formDependency.id) {
                dependencyExists = true;
            }
        });

        if (!dependencyExists) {
            this.deleteDependency(formDependency.id);
        }
    }

    getDataSourceFieldName( datasourceId, fieldId) {
        let fieldName = '';
        const ds = this._dataSources.get(datasourceId);
        if (ds) {
            _.forEach(ds.Fields.Field, (field) => {
               if (field.id === fieldId) {
                   switch (field.id) {
                       case 'CreateDate':
                           fieldName = 'Meta - Last Submission Date';
                           break;
                       case 'E':
                           fieldName = 'Meta - Instance Number';
                           break;
                       case 'FormInstanceGUID':
                           fieldName = 'Meta - Instance ID';
                           break;
                       case 'FormInstanceId':
                           fieldName = 'Meta - Link';
                           break;
                       case 'MajorVersion':
                           fieldName = 'Meta - Version Major';
                           break;
                       case 'MinorVersion':
                           fieldName = 'Meta - Version Minor';
                           break;
                       case 'Name':
                           fieldName = 'Meta - Instance Name';
                           break;
                       case 'ParentGUID':
                           fieldName = 'Meta - Parent Instance ID';
                           break;
                       case 'StageName':
                           fieldName = 'Meta - Stage';
                           break;

                       default:
                           fieldName = field.name.replace('|',' - ');
                           break;
                   }
               }
            });
        }

        return fieldName;
    }

    getDataSourceFieldDataType(datasourceId, fieldId) {
        let _field;
        let _dataType;
        const ds = this._dataSources.get(datasourceId);
        
        if (ds) {
            _field = ds.Fields.Field.filter((field) => field.id === fieldId);
            if (_field && typeof _field[0] !== 'undefined'){
                _dataType = _field[0].dataType;
            }
        }

        return _dataType;
    }

    addDependency( dependency ) {
        this._dependencies = this._dependencies.set( dependency.id, dependency);
    }

    getDependencies() {
        let _currentDependencies = [];
        let _commandTypesToLookup = [
            'connecttoconnection',
            'lookup',
            'playnew',
            'playreport',
            'getfieldfromform',
            'setfieldinform'
        ];
        let _controlFactoryKeysToLookup = [
            'control_play_form_button',
            'control_play_report_button'
        ];
        const filterCommands = (instance) => {
            return (instance.model && _commandTypesToLookup.indexOf(instance.model.get('type') > -1));
        };

        //Find dependencies in commands in behaviors
        const _filteredCommands = this._behaviorObjMap.filter(filterCommands);
        let _processedConnectionIds = [];
        let _processedActionIds = [];
        let _processedFormReportIds = [];
        _filteredCommands.map( (v,k) => {
            if (['connecttoconnection', 'lookup'].indexOf(v.model.get('type')) > -1){
                let connectionId = v.model.get('connectionId');
                let actionId = v.model.get('actionId');
                if (connectionId && _processedConnectionIds.indexOf(connectionId) === -1){
                    _currentDependencies.push({ 'id': connectionId, 'type': 'connection', 'caption': v.model.get('connectionName') });
                    _processedConnectionIds.push(connectionId);
                }
                if (actionId && _processedActionIds.indexOf(actionId) === -1){
                    _currentDependencies.push({ 'id': actionId, 'type': 'action', 'caption': v.model.get('actionName') });
                    _processedActionIds.push(actionId);
                }
            }else{
                let _id = '';
                let _caption = '';
                let _type = '';
                 if (['playnew', 'getfieldfromform', 'setfieldinform'].indexOf(v.model.get('type')) > -1){
                    _id = v.model.get('formGUID');
                    _type = 'form';
                    _caption = v.model.get('formName');
                }else if (v.model.get('type') === 'playreportbutton' || v.model.get('type') === 'playreport'){
                    _id = v.model.get('reportGUID');
                    _type = 'report';
                    _caption = v.model.get('reportName');
                }
                    
                if (_id && _processedFormReportIds.indexOf(_id) === -1){
                    _currentDependencies.push({ 'id': _id, 'type': _type, 'caption': _caption });
                    _processedFormReportIds.push(_id);
                }
            }            
        });

        //Find dependencies in controls
        const filterControls = (instance) => {
            return (instance.model && _controlFactoryKeysToLookup.indexOf(instance.model.get('factoryKey') > -1));
        };
        const _filteredControls = this._objMap.filter(filterControls);
        _filteredControls.map( (v,k) => {
            let _id = '';
            let _caption = '';
            let _type = '';
            if (v.model.get('factoryKey') === 'control_play_form_button'){
                _id = v.model.get('formGUID');
                _type = 'form';
                _caption = v.model.get('formName');
            }else if (v.model.get('factoryKey') === 'control_play_report_button'){
                _id = v.model.get('reportGUID');
                _type = 'report';
                _caption = v.model.get('reportName');
            }
                
            if (_id && _processedFormReportIds.indexOf(_id) === -1){
                _currentDependencies.push({ 'id': _id, 'type': _type, 'caption': _caption });
                _processedFormReportIds.push(_id);
            }                      
        });

        //Datasources
        const datasources = this.getDataSources();
        let _processedDatasourceIds = [];
        datasources.map( (v,k) => {
            let formGUID = v.formGUID;
            if (formGUID && _processedDatasourceIds.indexOf(formGUID) === -1){
                _currentDependencies.push({ 'id': formGUID, 'type': 'form', 'caption': v.formName });
                _processedDatasourceIds.push(formGUID);
            }
        });

        return _currentDependencies;
    }

    deleteDependency(id) {
        this._dependencies = this._dependencies.delete(id);
    }

    addReportingMetaData( metaData ) {
        this._reportingMetaData = this._reportingMetaData.set( metaData.id, metaData);
    }

    getReportingMetaData(id) {
        let metaData;

        if (id) {
            metaData = this._reportingMetaData.get(id);
        } else {
            metaData = this._reportingMetaData;
        }

        return metaData;
    }

    deleteReportMetaData( id ) {
        this._reportingMetaData = this._reportingMetaData.delete(id);
    }

    setReportingMetaData( dsId, model, addColumns, hasDataSource, createArguments = false ) {
        const pg = this.getPage( this.currentPageId );
        const factoryKey = model.get('factoryKey');

        let _isAggregate = 'true';
        if (factoryKey ===  'control_data_list'){ //PF-8620 - Report is showing only one instance when using identical values (scenario)
            _isAggregate = 'false';
        }

        const metadata = new reportingMetaData({
            id: model.get('id'),
            name: pg.model.get('name') + '|' + model.get('name'),
            dataSource: dsId,
            isAggregate: _isAggregate,
            ignoreNull: 'false'
        });
        
        const isFilterType = (factoryKey) => factoryKey === 'control_filter_input' || factoryKey === 'control_filter_selector';

        if (addColumns) {
            if (!metadata.Columns) {
                metadata.Columns = [];
            }

            const columns = model.get('Columns');

            if (columns && columns.Column &&  columns.Column.length>0){
                let count = 0;
                $.each(columns.Column, (index, column) => {
                    const _column = {
                        id: count,
                        aggregation: column.Properties.aggregation,
                        sortType: column.Properties.sortType,
                        sortOrder: column.Properties.sortOrder,
                        visible: column.Properties.visible,
                        Expression: {}
                     };
                     if(column.Properties.expression){
                        if(util.isChart(model.get('factoryKey'))){
                            _column.Expression.id = column.EXPR.id;
                            _column.Expression.type = column.EXPR.type;
                            // noinspection JSAnnotator
                            _column.Expression.Argument = column.EXPR.arguments;
                        }else{
                            _column.Expression =  column.Properties.expression;
                            _column.Expression.Argument = { type: 'reporting_datasourceField', value: column.Properties.fieldId, dataType: column.Properties.fieldDataType };
                        }
                     }
                     else if (createArguments && util.isChart(factoryKey) || isFilterType(factoryKey)) {
                         _column.Expression.Argument = {
                             type: 'reporting_datasourceField',
                             value: column.Properties.fieldId,
                             dataType: column.Properties.fieldDataType
                         };
                     }

                     metadata.Columns.push(_column);
                     count++;
                });
            }
        }
        let ds = this.getDataSources(dsId);
        if(ds && hasDataSource !== undefined &&  !hasDataSource){
             ds.uses += 1;
        }

        this.gridEl = constants.PROP_MAIN_TABLE;

        this.addReportingMetaData( metadata );
    }

    getSearchableItems() {
        var self = this;
        let items = [];
        var pageControls = Immutable.Map();
        var childControls = [];
        var controls = [];

        this.pageList.map( (v, k) =>{
            controls = [];
            items.push( {   name:v.model.get('name'), id: v.model.get('id'), type:'page', icon: '', displayName: v.model.get('name'), instanceId: ''} );

            pageControls = self.getPageControls(v.model.get('id'), undefined);

            pageControls.map( (vobj,kobj) => {
                childControls = undefined;
                items.push({
                    name:vobj.model.get('name'), 
                    caption: vobj.model.get('text'),
                    id: vobj.model.get('id'), 
                    type:'object', 
                    icon: '', 
                    displayName: v.model.get('name') + '/' + vobj.model.get('name'),
                    instanceId: '',
                    pageName:vobj.model.get('name'),
                    groupName:vobj._groupId ? this.getObjectName(vobj._groupId) : ''
                });

                // PF-1222: Adding caption from details table to the searches
                if (_.includes(['control_drop_down', 'control_list'], vobj.model.get('factoryKey'))) {
                    vobj.model.get('gridData').forEach((item)=>{
                        items.push({
                            name: vobj.model.get('name'), 
                            caption: item.caption,
                            id: vobj.model.get('id'),
                            type:'object', 
                            icon: '', 
                            displayName: v.model.get('name') + '/' + vobj.model.get('name') + '/' + item.caption, 
                            instanceId: ''
                        });
                    });
                }

            });

            let pageBehaviorInstances = Immutable.Map();
            pageBehaviorInstances = self.getPageBehaviorInstances(v.model.get('id'));

            pageBehaviorInstances.map( (vInstance,kInstance) => {
                let commandObjects = self.getBehaviorInstanceObjects(vInstance.get('id'), undefined);
                commandObjects.map( (vCommand,kCommand) => {

                    items.push( {   name: vCommand.model.get('name'), id: vCommand.model.get('id'), type:'command', icon: '', displayName: v.model.get('name') + '/' + vInstance.get('owner') + '/' + vCommand.model.get('name'), instanceId: vInstance.get('id') } );

                });
            });
        });

        // PF-1222: Adding workflow tab details to the list of searchable items
        let workflows = Immutable.Map();
        workflows = self.getWorkflowStages(false, false);
        workflows.forEach((workflow, key)=>{
            items.push({
                name: workflow.model.get('name'), 
                caption: workflow.model.get('text'),
                id: key,
                type: 'stage',
                icon: '', 
                displayName: workflow.model.get('name'), 
                instanceId: ''
            });
        });
        return items;
    }

    getAllBehaviorInstancesByPageList() {
        var self = this;
        let pages = [];
        this.pageList.map( (v, k) =>{
                pages.push({
                    id: v.model.get('id'),
                    name: v.model.get('name'),
                    number: v.model.get('pageNumber')
                })
            }
        );

        var pageBehaviorInstances = Immutable.Map();
        var controls = [];
        var childControls = [];

        _.forEach(pages , function(p) {
            pageBehaviorInstances = self.getPageBehaviorInstances(p.id);
            p.selectFields = [];
            controls = [];
            var prevOwner = '';
            var i = -1;

            pageBehaviorInstances.map( (v,k) => {
                childControls = undefined;
                var selectFields = undefined;


                if (v.get('owner') !== p.name) {

                    if (prevOwner === v.get('ownerId') ) {
                        controls[i].controls.push({
                            id: v.get('id'),
                            factoryKey: v.get('factoryKey'),
                            name: v.get('text'),
                            icon: v.get('icon'),
                            selectFields: (selectFields) ? selectFields : undefined,
                            controls: undefined
                        })
                    } else {
                        // sets Behavior Instance (Event) info
                        childControls = [{
                            id: v.get('id'),
                            factoryKey: v.get('factoryKey'),
                            name: v.get('text'),
                            icon: v.get('icon'),
                            selectFields: (selectFields) ? selectFields : undefined,
                            controls: undefined
                        }];

                        // Object that owns the Behavior Instance (Event)
                        controls.push({
                            id: v.get('id'),
                            factoryKey: v.get('factoryKey'),
                            icon: v.get('icon'),
                            name: v.get('owner'),
                            selectFields: (selectFields) ? selectFields : undefined,
                            controls: childControls
                        });

                        i++;

                        prevOwner = v.get('ownerId');
                    }


                } else {

                    controls.splice(0,0,{
                        id: v.get('id'),
                        factoryKey: v.get('factoryKey'),
                        icon: v.get('icon'),
                        name: v.get('text'),
                        selectFields: (selectFields) ? selectFields : undefined,
                        controls: childControls
                    });
                    i++;
                }
            });

            p.controls = controls;
        });

        console.log(pages);
        return pages;
    }

    getBehaviorInstanceObjectsByPageList(filter, includeForm=false, includeWorkflow=false) {
        var self = this;
        let pages = [];
        
        if (includeWorkflow) {
            pages.push({
                id: 'workflow',
                name: 'Workflow',
                number: 0
            })
        }

        if (includeForm) {
            pages.push({
                id: 'Form',
                name: self.mainKind==='R'? 'Report': 'App',
                number: 0
            })
        }

        this.pageList.map( (v, k) =>{
                pages.push({
                    id: v.model.get('id'),
                    name: v.model.get('name'),
                    number: v.model.get('pageNumber')
                })
            }
        );

        // getBehaviorInstanceObjects
        let pageBehaviorInstances = Immutable.Map();
        let controls = [];
        let childControls = [];

        _.forEach(pages , function(p) {
            pageBehaviorInstances = self.getPageBehaviorInstances(p.id, null, includeForm, includeWorkflow);
            p.selectFields = [];
            controls = [];
            var prevOwner = '';
            var prevBehKey = '';
            var i = -1;

            pageBehaviorInstances.map( (v,k) => {
                childControls = undefined;
                var selectFields = undefined;


                // Check if this Instance is owned by the current Page
                // Process Page / Object ownership here
                if (v.get('owner') !== (p.id==='Form' ? p.id : p.name)) {

                    if (prevOwner === v.get('ownerId') && prevBehKey === v.get('behaviorKey') ) {
                        let commandObjects = self.getBehaviorInstanceObjects(v.get('id'), filter);
                        commandObjects.map( (vCommand,kCommand) => {

                            childControls = {
                                id: vCommand.model.get('id'),
                                factoryKey: vCommand.model.get('factoryKey'),
                                name: vCommand.model.get('name'),
                                icon: vCommand.model.get('icon'),
                                selectFields: (selectFields) ? selectFields : undefined,
                                controls: undefined,
                                behaviorKey:vCommand.model.get('behaviorKey'),
                                ownerId:vCommand.model.get('ownerId'),
                                text:vCommand.model.get('text')
                            };

                            controls[i].controls.push(childControls);
                        })
                    } else {
                        // sets Behavior Instance (Event) info (i.e. Text is clicked)
                        // Need to put the Command Object info here
                        let commandObjects = self.getBehaviorInstanceObjects(v.get('id'), filter);
                        commandObjects.map( (vCommand,kCommand) => {
                            let instanceInfo = self.getBehaviorInstance(vCommand.behaviorInstanceId);
                            let controlInfo = self.getObject(instanceInfo.get('ownerId'));
                            let isWorkflow = instanceInfo.get("behaviorKey") === "workflow";
                            let isTableColumn = false;
                            let tableInfo = null;
                            let columnInfo = null;
                            if (!controlInfo && !isWorkflow) {  // should be a column on a table... So look fo the table
                                let result =self.getTableInfo(instanceInfo.get('pageId'), instanceInfo.get('ownerId'));
                                tableInfo = result.tableInfo;
                                isTableColumn = true;
                                columnInfo = result.selectedColumn;
                            }
                            childControls = [{
                                id: vCommand.model.get('id'),
                                factoryKey: vCommand.model.get('factoryKey'),
                                name: vCommand.model.get('name'),
                                icon: vCommand.model.get('icon'),
                                selectFields: (selectFields) ? selectFields : undefined,
                                controls: undefined,
                                behaviorKey:v.get('behaviorKey'),
                                ownerId:  v.get('id'),
                                controlId: controlInfo? controlInfo.model.get('id') : instanceInfo.get('ownerId'),
                                isTableColumn : isTableColumn,
                                tableInfo : isTableColumn? tableInfo.model.get('id'): null,
                                columnInfo : isTableColumn? columnInfo[0]: null,
                                text:vCommand.model.get('text')
                            }];
                            // Object that owns the Behavior Instance (Event) (i.e. Text, Button)
                            controls.push({
                                id: v.get('id'),
                                factoryKey: v.get('factoryKey'),
                                icon: v.get('icon'),
                                name: v.get('owner'),
                                selectFields: (selectFields) ? selectFields : undefined,
                                controls: childControls,
                                behaviorKey:v.get('behaviorKey'),
                                ownerId: v.get('ownerId'),
                                controlId: controlInfo? controlInfo.model.get('id') : instanceInfo.get('ownerId'),
                                isTableColumn : isTableColumn,
                                tableInfo : isTableColumn? tableInfo.model.get('id'): null,
                                columnInfo : isTableColumn? columnInfo[0]: null,
                                text:v.get('text')
                            });
                            i++;
                        });
                        prevBehKey = v.get('behaviorKey')
                        prevOwner = v.get('ownerId');
                    }
                } else {
                    // Process Page Ownership Here
                    let commandObjects = self.getBehaviorInstanceObjects(v.get('id'), filter);
                    commandObjects.map( (vCommand,kCommand) => {
                        childControls = [{
                            id: vCommand.model.get('id'),
                            factoryKey: vCommand.model.get('factoryKey'),
                            name: vCommand.model.get('name'),
                            icon: vCommand.model.get('icon'),
                            selectFields: (selectFields) ? selectFields : undefined,
                            controls: undefined,
                            behaviorKey:vCommand.model.get('behaviorKey'),
                            ownerId:vCommand.model.get('ownerId'),
                            controlId: v.get('ownerId'),
                            text:vCommand.model.get('text')
                        }];

                        controls.splice(0,0,{
                            id: v.get('id'),
                            factoryKey: v.get('factoryKey'),
                            icon: v.get('icon'),
                            name: v.get('text').replace("Form", "App"),
                            selectFields: (selectFields) ? selectFields : undefined,
                            controls: childControls,
                            behaviorKey:v.get('behaviorKey'),
                            ownerId:v.get('ownerId'),
                            controlId: v.get('ownerId'),
                            text:v.get('text').replace("Form", "App")
                        });
                        i++;
                    });
                }

            });
            p.controls = controls;
        });
        return pages;
    }

    get objectCount() {	return this._objMap.size; }

	get objectList() { return this._objMap;	}

    get behaviorObjectList() { return this._behaviorObjMap;	}


    //========================================================
    // Focus id
    //========================================================

	get currentId() { return this._currentId;  }

	set currentId( id ) { this._currentId = id; }

    get selectedAsset() { return this._selectedAsset;  }

    set selectedAsset( asset ) { this._selectedAsset = asset; }

    get currentBehaviorId() { return this._currentBehaviorId;  }

    set currentBehaviorId( id ) { this._currentBehaviorId = id; }

    //PF-2456 - Resize event is not releasing always
    //========================================================
    // Resizing control
    //========================================================
    get isResizing() { return this._isResizing; }
    set isResizing(arg) { this._isResizing = arg; }

    //========================================================
    // Multi-selected controls hash
    //========================================================
     get formItemSelected() { return this._formItemSelected; }
     set formItemSelected(arg) { this._formItemSelected = arg; }

     get reportItemSelected() { return this._reportItemSelected; }
     set reportItemSelected(arg) { this._reportItemSelected = arg; }

     get connectionItemSelected() { return this._connectionItemSelected; }
     set connectionItemSelected(arg) { this._connectionItemSelected = arg; }

     get userItemSelected() { return this._userItemSelected; }
     set userItemSelected(arg) { this._userItemSelected = arg; }

     get activeDashboardTab() { return this._activeDashboardTab; }
     set activeDashboardTab(arg) { this._activeDashboardTab = arg; }


    get isDrawingMultiSelection() { return this._isDrawingMultiSelection; }
    set isDrawingMultiSelection( arg ) { this._isDrawingMultiSelection = arg; }

    get isFromMultSelectionGroup() { return this._isFromMultSelectionGroup; }
    set isFromMultSelectionGroup( arg ) { this._isFromMultSelectionGroup = arg; }

    addToSelected(id ) {
        const control = this.mainKind !== 'M' ? this.getObject(id) : mobileListService.getMobileControl(id);
        this._selected = this._selected.set( id, control );

        if (this._selected.size === 1) {
            const obj = this.getObject( id );
            if (obj && obj.model.attributes.factoryKey === 'control_group') {
                $('#designerAddGroup').text('Remove Group');
            }
        } else {
            $('#designerAddGroup').text('Add Group');
        }
    }

    deleteFromSelected( id ) {
        this._selected = this._selected.delete( id );
    }

    isSelected( id ) {
        return  this._selected.has( id );
    }

    isCanvasSelected() { return  this.currentId === 'canvas'; }

    clearSelected( ) {
        if(this._selectedAsset){
            this._selectedAsset = null;
        }
        this._selected = this._selected.clear();
        this._isTableColumnSelected = false;
        $('#designerAddGroup').text('Add Group');
    }

    get selectedCount() { return this._selected.size; }

    get selected() { return this._selected; }

    get isTableColumnSelected() { return this._isTableColumnSelected; }
    set isTableColumnSelected( arg ) { this._isTableColumnSelected = arg; }

    get areThereSelectedCanvasObjects() {
        return (this.fcanvas && (this.fcanvas.getActiveObject() || this.fcanvas.getActiveGroup())) ||
            this.selected.some((elem)=>
                {
                    return elem.model.get('factoryKey') ==='control_timer'
                });
    }
    
    getControlsByCurrentPage(){
        const filter = (object) => {
            return (object._pageId === this.currentPageId  && object.id !== constants.FORM_RAW_ID);
        };
        return this._objMap.filter(filter);
    }
    
    //==================================================================================
    // Replaces list of "selected" with a sorted rendition.
    // Sorts ascending by element NW corner origin.
    //==================================================================================
    sortSelected() {
        this._selected = this._selected.sort( (v1, v2) => {

            const pos1 = { left: v1.model.attributes.left, top: v1.model.attributes.top };
            const pos2 = { left: v2.model.attributes.left, top: v2.model.attributes.top };

            const d1 = util.square(pos1.left) + util.square(pos1.top);
            const d2 = util.square(pos2.left) + util.square(pos2.top);

            return d1 < d2 ? -1 : (d1 === d2 ? 0: +1);
        });
        return this._selected;
    }


    //========================================================
    // Clipboard of controls
    //========================================================
    copySelected() {
        this._clipboard =  Immutable.Map();
        // PF-3937 Restarting Count and store it locally so the count is not increased on each copy
        if(!this._currentCopiedRadioButtonCount) {
            this._currentCopiedRadioButtonCount = sequenceService.getRadioNumber();
        }

        // PF-3937 storing value for radio button group so it can be modified by its factory by taking 3 radio buttons.
        let currentRadioButtonCount=sequenceService.getRadioNumber();
        // Do not copy the object.  Clone it
        this._selected.forEach( element => {
            const factoryKey = factoryService.factories[element.model.get('factoryKey')];
            const elementView = factoryKey.factory.createView(element.model.get('factoryKey'), element.model.get('pos'));
            // PF-3937 If is bugger then reset the count to the one stored locally.
            if(elementView.model.get('factoryKey') ===  'control_radio_button'){
                if(this._currentCopiedRadioButtonCount && this._currentCopiedRadioButtonCount < sequenceService.getRadioNumber()){
                    sequenceService.setRadioNumber(this._currentCopiedRadioButtonCount);
                    elementView.model.attributes.value = sequenceService.getRadioNumber();
                    elementView.model.attributes.text = 'Option '+ sequenceService.getRadioNumber();
                    this._currentCopiedRadioButtonCount = currentRadioButtonCount;
                }
            }
            if(elementView.model.get('factoryKey') ===  'control_radio_button_group'){
                sequenceService.setRadioNumber(currentRadioButtonCount);
            }

            for (let p in element.model.attributes) {
                if ( element.model.attributes.hasOwnProperty(p) && (element.model.attributes[p] !== undefined) ) {
                    elementView.model.attributes[p] = objectUtil.advancedCopy(element.model.attributes[p]);
                }
            }
            if (element.model.attributes.Property){
                const prop = objectUtil.advancedCopy(element.model.attributes.Property);
                element.model.attributes.Property = prop;
            }

            elementView.model.set('pageId', element._pageId, { silent: true });
            elementView._pageId = element._pageId;
            elementView.options.pos = element.options.pos;

            // Copy also behaviors and connections
            elementView.behaviorInstance = Immutable.Map();
            elementView.behaviorObjects = Immutable.Map();
            elementView.connectionObjects = Immutable.Map();

            this.copyObjectBehaviors(element.model.get('id'), elementView);
            const childList =element.model.get('childList');
            if ( childList ){
                this._copyGroupChildren(element.model, elementView.model);
                const childListResult = elementView.model.get('childList');
                for (let i = 0;  i<childList.length; ++i) {
                    const childView =childListResult[i];// this.getObject (childListResult[i].get('id'));
                    this.copyObjectBehaviors(childList[i].get('id'), childView );
                }
                // PF-4386: Ordering (1/2): Overwrite original zIndex value for further sorting
                elementView.model.set('zOrder', element.model.get('zOrder'), constants.MODEL_SILENT);
            }

            this._clipboard = this._clipboard.set(elementView.model.get('id'), elementView);
        });
        let currentSize = this.selectedCount;
        util.validateMenuItems(currentSize);
     }

     copyObjectBehaviors(elementId, elementView) {
        let behaviorToCopy = this.getBehaviorInstanceByObject(elementId);
        behaviorToCopy.map( behaviorInstance => {
            let bi = behaviorInstance.get('id');
            let behaviorIn = new BehaviorInstanceModel({
                id: bi,
                behaviorKey: behaviorInstance.get('behaviorKey'),
                text: behaviorInstance.get('text'),
                open: behaviorInstance.get('open'),
                owner: behaviorInstance.get('owner'),
                ownerId: behaviorInstance.get('ownerId'),
                pageId: behaviorInstance.get('pageId')
            });
            for (let p in behaviorInstance) {
                if ( behaviorInstance.hasOwnProperty(p) && (behaviorInstance[p] !== undefined)) {
                    behaviorIn[p] = objectUtil.advancedCopy(behaviorInstance[p]);
                }
            }
            if (!elementView.behaviorInstance) {
                elementView.behaviorInstance = new Immutable.Map();
            }
            elementView.behaviorInstance = elementView.behaviorInstance.set(bi, behaviorIn );

            let behaviorObjects = this.getBehaviorInstanceObjects( bi );
            behaviorObjects.map( behavior => {
                let pos = {left: behavior.model.get('x'), top: behavior.model.get('y')};
                let b = new CommandGenericView( { model: behavior.model,  el: $(constants.FORM_ID),
                    pos: pos, factoryKey: behavior.model.get('factoryKey'), id: behavior.model.get('id'),
                    type: 'behavior' } );
                let model = behavior.model.attributes;
                for (let p in model) {
                    if ( model.hasOwnProperty(p) && (model[p] !== undefined)) {
                        if (p!=='widget') {
                            b.model.attributes[p] = objectUtil.advancedCopy(model[p]);
                        } else {
                            b.model.attributes[p] = model[p];
                        }
                    }
                }
                b.behaviorInstanceId = behavior.behaviorInstanceId;
                if (!elementView.behaviorObjects) {
                    elementView.behaviorObjects = new Immutable.Map();
                }
                elementView.behaviorObjects = elementView.behaviorObjects.set(behavior.model.get('id'), b );
            });

            const conns = this.behaviorConnections.filter(
                        connection => connection.scriptId === bi ).valueSeq().toArray();
            if (!elementView.connectionObjects) {
                elementView.connectionObjects = new Immutable.Map();
            }
            conns.map( connection => {
                elementView.connectionObjects = elementView.connectionObjects.set(connection.model.attributes.id,
                                                            Object.assign({}, connection) );
            });
        });
     }

    copySelectedWorkflowObjects(selectedWorkflowObjects){
        this._workflowClipboard = Object.assign({}, selectedWorkflowObjects);
        if (selectedWorkflowObjects.model) {
            this._workflowClipboard.model = objectUtil.advancedCopyWorkflows(selectedWorkflowObjects).model;
            // And nothing else.  There is no way to have behaviors here since they are associated with a path
            // and we need at least 2  workflows for having a path
        } else {
            let objects = [];
            selectedWorkflowObjects.objects.map( object => {
                let o = Object.assign({}, object);
                o.model = objectUtil.advancedCopyWorkflows(object).model;
                // Copy also behaviors and connections
                o.behaviorInstance = Immutable.Map();
                o.behaviorObjects = Immutable.Map();
                o.connectionObjects = Immutable.Map();
                this.copyObjectBehaviors(o.model.get('id'), o);
                objects.push(o);
            });
            this._workflowClipboard._objects = objects;
        }
        let currentSize = this.selectedCount;
        util.validateMenuItems(currentSize);
    }

    copySelectedBehaviors(selectedBehaviors){
        this._behaviorClipboard = Object.assign({}, selectedBehaviors);
        if (selectedBehaviors.model) {
            this._behaviorClipboard.model = objectUtil.advancedCopyBehaviors(selectedBehaviors).model;
        } else {
            const objects = [];
            selectedBehaviors._objects.map( object => {
                const o = Object.assign({}, object);
                o.model = objectUtil.advancedCopyBehaviors(object).model;
                objects.push(o);
            });
            this._behaviorClipboard._objects = objects;
        }
        const currentSize = this.selectedCount;
        util.validateMenuItems(currentSize);
    }

    _copyGroupChildren(sourceGroupModel, targetGroupModel) {
        const doNotCopy = Immutable.Set(['zOrder','id','childList']);
        targetGroupModel.attributes.childList = [];


        if (sourceGroupModel.get('childList')) {
            Immutable.List(sourceGroupModel.get('childList')).map(child => {
                    const _fp = factoryService.factories[child.get('factoryKey')];
                    const vw = _fp.factory.createView(child.get('factoryKey'), child.get('pos'));

                    for (let p in child.attributes) {
                        if (child.attributes.hasOwnProperty(p) && (child.attributes[p] !== undefined) && !doNotCopy.contains(p)) {
                            //  creating new instances of properties which are objects or maps
                            vw.model.attributes[p] = objectUtil.advancedCopy(child.attributes[p]);
                        }
                    }
                    if (child.attributes.Property) {
                        let prop = objectUtil.advancedCopy(child.attributes.Property);
                        child.attributes.Property = prop;
                    }
                    // PF-4386: Ordering (1/2): Overwrite original zIndex value for further sorting
                    vw.model.set('zOrder', child.get('zOrder'), constants.MODEL_SILENT);

                    vw.model.set('attrId', new Date().getTime(), {silent: true});
                    vw.model.attributes.groupId = targetGroupModel.get('id');
                    vw.groupId = targetGroupModel.get('id');

                    const childView = this.getObject(child.get('id'));
                    if (childView) {
                        this.pasteObjectBehaviorInstances(childView, vw.id);
                    }

                    //this.addObject(vw.id, vw);
                    this._copyGroupChildren(child, vw.model);

                    targetGroupModel.get('childList').push(vw.model);
            });

        }
    }

    // PF-3065 Function that creates new factory for each child.
    pasteGroupChildren(sourceGroupModel, targetGroupModel){
        targetGroupModel.attributes.childList =[] ;
        let doNotCopy = Immutable.Set(['zOrder','id']);

        if (sourceGroupModel.get('childList')) {
            Immutable.List(sourceGroupModel.get('childList')).map(child => {

                const factoryParams = factoryService.factories[child.attributes.factoryKey];
                const control = factoryParams.factory.createView(child.attributes.factoryKey,child.get('pos'));

                for (let p in child.attributes) {
                    if ( child.attributes.hasOwnProperty(p) && (child.attributes[p] !== undefined) && !doNotCopy.contains(p)) {
                        //  creating new instances of properties which are objects, arrays or maps
                        control.model.attributes[p] = objectUtil.advancedCopy(child.attributes[p]);
                    }
                }
                if (child.attributes.Property){
                    let prop = objectUtil.advancedCopy(child.attributes.Property);
                    child.attributes.Property = prop;
                }
                // PF-4386: Maintain old zIndex for further ordering
                control.model.set('zOrder', child.get('zOrder'), constants.MODEL_SILENT);

                control.model.set('attrId', new Date().getTime(), {silent: true});
                control.model.attributes.groupId = targetGroupModel.get('id');
                control.groupId = targetGroupModel.get('id');
                targetGroupModel.get('childList').push(control.model);//
                this.addObject(control.id, control);
                const childView =child;// this.getObject(child.get('id'));
                if (childView) {
                    this.pasteObjectBehaviorInstances(childView, control.id);
                }
                this.pasteGroupChildren(child,control.model)
            });
        }
    }


    paste() {
        $('#aPage').css({opacity : 0.5});
        let spinner = new Spinner().spin($(constants.CONTENT_ID).get(0));

        window.setTimeout(() => {
            let spinnerWillDisplay = false;
            this.clearSelected(); 

            let pasteElem = (element) => {
                
                if (!element){ return;} 

                let doNotCopy;
                if (element.model && (element.model.get('factoryKey') === 'control_radio_button_group' ||
                    element.model.get('factoryKey') === 'control_checkbox_group')) {
                    doNotCopy = Immutable.Set(['zOrder','id']);
                }
                else {
                    doNotCopy = Immutable.Set(['zOrder','id','childList']);
                }

                const factoryParams = factoryService.factories[element.options.factoryKey];
                let maxWidth = $('#aPageSel').width();
                let maxHeight = $('#aPageSel').height();
                let elementWidth = util.extractNum(element.model.get('width'));
                let elementHeight = util.extractNum(element.model.get('height'));

                const top = util.extractNum(element.model.get('top'));
                const left = util.extractNum(element.model.get('left'));
                let pageId = objectUtil.getObjectPageId(element);
                
                let dropPos = {
                    top:  (top + 10 + elementHeight) > maxHeight || this.currentPageId !== pageId ? top : top + 10,
                    left: (left +10 + elementWidth) > maxWidth || this.currentPageId !== pageId ? left : left + 10
                };

                // Updating the current pasted position for PF-1721
                if (!element.options.pos) {
                    element.options.pos = {top, left};
                } 

                element.options.pos.top = dropPos.top;
                element.options.pos.left = dropPos.left;

                // PF-2223:
                // Setting new values for the elements, so on next paste, the stuff gets positioned correctly
                element.model.set('pageId', this.currentPageId, {silent:true});

                const control = factoryParams.factory.createView(element.options.factoryKey, dropPos);

                for (let p in element.model.attributes) {
                    if ( element.model.attributes.hasOwnProperty(p) && (element.model.attributes[p] !== undefined) && !doNotCopy.contains(p) ) {
                        //  creating new instances of properties which are objects, arrays or maps
                        control.model.set(p, objectUtil.advancedCopy(element.model.get(p)), constants.MODEL_SILENT);
                    }
                }

                if (element.model.attributes.Property){
                    let prop = objectUtil.advancedCopy(element.model.attributes.Property);
                    element.model.set('Property', prop, constants.MODEL_SILENT);
                }

                // PF-4386: Sorting first step (1/2): Adding the original order
                 control.model.set('zOrder',element.model.get('zOrder'), constants.MODEL_SILENT);

                //Group Children
                // examine the Children and Create Views, assign models to new Group Model.
                // This needs to be Recursive.
                if (element.model.get('childList')) {
                    // PF-3065 Adding new function to paste children also.
                    this.pasteGroupChildren(element.model, control.model);
                }

                // PF 1151 this was keeping the residual position on copy and pasting groups
                // to new pages. we should be using model setters and getters too

                // PF-3065 Adding top and left to the element copied so it can be cascaded.
                element.model.set('top', dropPos.top, {silent: true});
                element.model.set('left', dropPos.left, {silent: true});

                control.model.set('top', dropPos.top, {silent: true});
                control.model.set('left', dropPos.left, {silent: true});
                control.model.set('id', control.id, {silent:true});
                control.model.set('attrId', new Date().getTime(), {silent: true});
                // ***************************************
                control.model.resetLastState();

                const childList = [];
                const childListView = [];
                if (control.model.get('childList')) {
                    _.forEach(control.model.get('childList'), child => {
                        childList.push(child);
                        childListView.push(this.getObject(child.get('id')));
                    });
                }

                this.addObject(control.id, control);
                control.delegateEvents();
                const renderOpts = {skipEventRegistration: true};
                control.render(renderOpts);
                // Removed for Solve PF-2459
                //this.clearSelected();

                this.currentId = control.id;

                focusController.selectionRectangle( element.model.get('id'), false );
                this.addToSelected(control.id);
                focusController.selectionRectangle( control.id, true );

                this.pasteObjectBehaviorInstances(element, control.id);

                let parentObj = this;
                let elementAux = element;
                let currentDataSourceId = control.model.get('dataSourceId');


                if (this._clipboard.size === 1) {
                    // Paint the properties
                    focusController.switchPropsViews();
                    // Focus on default property tab
                    $('#settings-tab a').click();
                    let childListView=[];
                    let childList=[];
                    if (control.model.get('childList')) {
                        _.forEach(control.model.get('childList'), child => {
                            childList.push(child);
                            childListView.push(this.getObject(child.get('id')));
                        });
                    }

                    undoService.add({
                        undo() {
                            if (control.model.get('childList')) {
                                control.model.set('childList', childList, {silent:true});
                                _.forEach(control.model.get('childList'), child => {
                                    //PF-3485 Deleting the event raise, as destroy object will do the work.
                                    parentObj.destroyObject(child.id, { silent: false });
                                })
                            }
                            parentObj.destroyObject(control.id, {silent: false});
                            control.unrender();
                        },

                        redo() {
                            parentObj.addObject(control.id, control, {pageId: control.model.get('pageId')});
                            control.listenTo(control.model, 'change', control.render);
                            control.delegateEvents();
                            control.render();
                            parentObj.clearSelected();
                            focusController.selectionRectangle(elementAux.model.get('id'), false);
                            parentObj.addToSelected(control.id);
                            focusController.selectionRectangle(control.id, true);

                            //  PF-2704 Processing for CheckboxGroup and RadioGroup//
                            if (childList) {
                                control.model.set('childList', childList, {silent:true});
                                _.forEach(childListView, child => {
                                    child.listenTo(child.model, 'change', child.render);
                                    parentObj.addObject(child.id, child, {pageId: child.model.get('pageId')});
                                    child.render();
                                })
                            }
                            parentObj.pasteObjectBehaviorInstances(elementAux, control.id);
                        }
                    }, textConstants.UNDO_PASTE);
                }

                if(this.mainKind === constants.KIND_REPORT){
                    if (currentDataSourceId) {
                        this.setReportingMetaData(currentDataSourceId, control.model, true, true, true);
                    }   
                }

                utilMobile.addedNewObjectToDesktopCanvas(control);
            }

            let loopControls = (array) => {
                let nextIndex = 0;
                
                return {
                   next: function() {
                       return nextIndex < array.length ?
                           { element: array[nextIndex++], done: false} :
                           { done: true};
                   }, 
                   index: function() {
                       return nextIndex;
                   }                   
                };
            }

            let it = new loopControls(this._clipboard.toArray());

            let traversing = () => {
                let control = it.next(); 

                if (control.done) {
                    return;
                }
                
                pasteElem(control.element);
                traversing();
            }

            traversing();

            //PF-3065 Commenting this line as it overwrites the current copied object.
            //PF-2391 allow multiple pasting works as cascade
            //this.copySelected();

            let theParent = this;
            if (this._clipboard.size > 1) {
                // Paint the properties
                focusController.switchPropsViews();

                // Focus on default property tab
                $('#settings-tab a').click();

                let auxMap= Immutable.Map(theParent._selected);
                undoService.add({
                        undo() {
                            $('#aPage').css({opacity : 0.5});
                            const spinner = new Spinner().spin($(constants.CONTENT_ID).get(0));
                            window.setTimeout(() => {
                                auxMap.map( control => {
                                    if (control.model.get('childList')) {
                                        _.forEach(control.model.get('childList'), child => {
                                            $(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['delete', 'object', child.get('id'), '', control._pageId]);
                                        })
                                    }
                                    theParent.destroyObject(control.id, {silent: false});
                                    control.unrender();
                                });
                                spinner.stop();
                                $('#aPage').css({opacity :1});
                                eventBus.trigger(constants.EV_UPDATE_BEHAVIORS_ONLY);
                            }, 0);
                        },
                        redo() {
                            $('#aPage').css({opacity : 0.5});
                            const spinner = new Spinner().spin($(constants.CONTENT_ID).get(0));
                            window.setTimeout(() => {
                                auxMap.map( control => {
                                    theParent.addObject(control.id, control, {pageId: control.model.get('pageId')});
                                    control.listenTo(control.model, 'change', control.render);
                                    control.delegateEvents();
                                    control.render();
                                    focusController.selectionRectangle(control.model.get('id'), false);
                                    theParent.addToSelected(control.id);
                                    focusController.selectionRectangle(control.id, true);
                                    if (control.model.get('childList')) {
                                        _.forEach(control.model.get('childList'), child => {
                                            $(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['add', 'object', child.get('id'), child.get('name'), control._pageId]);
                                        })
                                    }
                                    theParent.pasteObjectBehaviorInstances(control, control.id);
                                });

                                spinner.stop();

                                $('#aPage').css({opacity :1});
                                eventBus.trigger(constants.EV_UPDATE_BEHAVIORS_ONLY);
                            }, 0);
                        }
                    }, textConstants.UNDO_PASTE_MULTIPLE);
                }

                // PF-4386: Ordering second step (2/2): Sort and re-index the zOrder
                const sortedList = alignmentController.sortByZOrderAsc(this._selected.filter(item=>!item.model.get('childList')), true);
                const haveGroups = this._selected.filter(item=>item.model.get('childList'));

                for (let singleItem of sortedList.values()) {
                    singleItem.model.set('zOrder', sequenceService.nextZIndex(), constants.MODEL_SILENT);
                }

                // PF-4386: Ordering group size
                if (haveGroups.size >= 1) {
                    for (let groupData of haveGroups.values()) {
                        const dataSorted = groupData.model.get('childList').concat(groupData.model).sort((a,b)=>{
                            return Number(a.get('zOrder')) > Number(b.get('zOrder'));
                        });

                        dataSorted.forEach((oneItem)=>{
                            oneItem.set('zOrder', sequenceService.nextZIndex(), constants.MODEL_SILENT);
                        });
                    }
                }

                $('#aPage').css({opacity: 1});
                eventBus.trigger(constants.EV_UPDATE_BEHAVIORS_ONLY);

                if (spinner) { 
                    spinner.stop();
                }
        }, 100);
    }    

    pasteInsideGroup(groupView){
        this.clearSelected();
        let enablePaste = true;
        let itemsGroup = groupView.model.get('childList');
        let typeOfGroups = ['control_group','control_radio_button_group','control_checkbox_group'];
        this._clipboard.map( element => {
            let supportedChildren = (childList, factoryKey) => childList.every(_object => _object.get('factoryKey') === factoryKey);
            let groupType;
            enablePaste = !this.controlHasSection(groupView.model.get('id'));
            
            if (!enablePaste){
                if(supportedChildren(groupView.model.get('childList'), 'control_checkbox')){
                    groupType = 'control_checkbox_group';
                }else if (supportedChildren(groupView.model.get('childList'), 'control_radio_button')){
                    groupType = 'control_radio_button_group';
                }

                if (['control_checkbox', 'control_radio_button'].indexOf(element.options.factoryKey) !== -1){
                    enablePaste = (element.options.factoryKey === 'control_checkbox' && groupType === 'control_checkbox_group')
                                || (element.options.factoryKey === 'control_radio_button' && groupType === 'control_radio_button_group');
                    if (enablePaste){
                        utilMobile.deleteElementIntoMobileObject(groupView.model.get('id'));
                        utilMobile.addedNewObjectToDesktopCanvas(groupView);
                    }
                }
            }

            if (enablePaste){
                const doNotCopy = Immutable.Set(['zOrder','id','childList']);
                const factoryParams = factoryService.factories[element.options.factoryKey];


                const dropPos = {
                    top: typeOfGroups.indexOf(element.model.attributes.factoryKey) < 0 ? 15 : 0,
                    left: typeOfGroups.indexOf(element.model.attributes.factoryKey) < 0 ? 15 : 0
                };

                // Updating the current pasted position for PF-1721
                element.options.pos.top=dropPos.top;
                element.options.pos.left=dropPos.left;

                const control = factoryParams.factory.createView(element.options.factoryKey, dropPos);

                for (let p in element.model.attributes) {
                    if ( element.model.attributes.hasOwnProperty(p) && (element.model.attributes[p] !== undefined) && !doNotCopy.contains(p) ) {
                        //  creating new instances of properties which are objects, arrays or maps
                        control.model.attributes[p] = objectUtil.advancedCopy(element.model.attributes[p]);
                    }
                }
                if (element.model.attributes.Property){
                    let prop = objectUtil.advancedCopy(element.model.attributes.Property);
                    element.model.attributes.Property = prop;
                }

                //Group Children
                // examine the Children and Create Views, assign models to new Group Model.
                // This needs to be Recursive.
                this._copyGroupChildren(element.model, control.model);
                control._groupId = groupView.id;

                // PF 1151 this was keeping the residual position on copy and pasting groups
                // to new pages. we should be using model setters and getters too

                control.model.set('top', dropPos.top, {silent: true});
                control.model.set('left', dropPos.left, {silent: true});
                control.model.set('id', control.id, {silent:true});
                if (typeOfGroups.indexOf(element.model.attributes.factoryKey) > -1){
                    let width = util.extractNum(control.model.attributes.width);
                    let height = util.extractNum(control.model.attributes.height);
                    control.model.set('width', width-3, {silent: true});
                    control.model.set('height', height-3, {silent: true});
                }


                // ***************************************

                this.addObject(control.id, control);
                utilMobile.addedNewObjectToDesktopCanvas(control);
                
                control.delegateEvents();
                control.render();
                itemsGroup.push(control.model);
                // Removed for Solve PF-2459
                //this.clearSelected();
                focusController.selectionRectangle( element.model.get('id'), false );
                this.addToSelected(control.id);
                focusController.selectionRectangle( control.id, true );

                this.pasteObjectBehaviorInstances(element, control.id);
                var parentObj = this;
                var elementAux = element;
                undoService.add({
                    undo() {
                        if (control.model.get('childList')){
                            _.forEach(control.model.get('childList'), child => {
                                $(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['delete', 'object', child.get('id'), '', control._pageId]);
                            })
                        }
                        parentObj.destroyObject(control.id, { silent: false });
                        control.unrender();
                    },
                    redo() {
                        parentObj.addObject(control.id, control);
                        control.delegateEvents();
                        control.render();
                        parentObj.clearSelected();
                        focusController.selectionRectangle(elementAux.model.get('id'), false);
                        parentObj.addToSelected(control.id);
                        focusController.selectionRectangle(control.id, true);
                        if (control.model.get('childList')) {
                            _.forEach(control.model.get('childList'), child => {
                                $(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['add', 'object', child.get('id'), child.get('name'), control._pageId]);
                            })
                        }
                        parentObj.pasteObjectBehaviorInstances(elementAux, control.id);
                    }
                }, textConstants.UNDO_PASTE);
            }else{
                let typeOfGroup1, typeOfGroup2;
                if(groupType === 'control_checkbox_group'){
                    typeOfGroup1 = 'checkboxes';
                    typeOfGroup2 = 'Checkbox';
                }else if(groupType === 'control_radio_button_group'){
                    typeOfGroup1 = 'radio buttons';
                    typeOfGroup2 = 'Radio button';
                }
                notificationService.info(`Only ${typeOfGroup1} can be added to ${typeOfGroup2} Groups used in Mobile app design`);
            }
        });

        if (enablePaste){
            groupView.model.set('childList', itemsGroup, {silent: true} );
            groupView.model.resetLastState();
            groupView.render();
        }
    }
    //========================================================
    // Paste element or elements into canvas position from
    // which they were cut or copied.
    //========================================================
    pasteSelected() {
        let selected = this.selected.first();
        if(this.selectedCount === 1 && selected instanceof GroupControlView){
            //Condition above is true but we are not copying elements inside the group, we are copying the groupd itself. 
           if(this._clipboard.size === 1 && this._clipboard.first().model.get('id') === selected.model.get('id') ){
               this.paste();  
           }else{
               this.pasteInsideGroup(selected);
           }
        }
        else{       
           this.paste();  
        }      
    }

    pasteObjectBehaviorInstances(source, targetControlId) {
        // First clone Instances
        const srcBehaviorInst = source.behaviorInstance;
        if (srcBehaviorInst) {
            srcBehaviorInst.map( instance => {
                const newInstance = instance.clone();
                newInstance.unset('parsedItem', {silent: true});
                //newInstance.id = sequenceService.nextDomId();
                newInstance.set({'id': sequenceService.nextDomId(), 'ownerId': targetControlId, 'pageId': this.currentPageId}, {silent: true});
                this.addBehaviorInstance( newInstance );

                // second clone Commands associated with each Instance
                let cloneMap = Immutable.Map();
                const srcCommands = source.behaviorObjects.filter( c => c.behaviorInstanceId === instance.id );
                srcCommands.map( cmd => {
                    const control = this.cloneBehaviorObject(cmd);
                    cloneMap = cloneMap.set(cmd.model.get('id'), control.id);
                    this.addBehaviorObject(control.id, control);
                });

                // Third clone connections
                let newConnections = [];
                const conns = source.connectionObjects.filter( connection => connection.scriptId === instance.id ).valueSeq().toArray();
                this.cloneBehaviorConnections(conns, cloneMap, newConnections);
            });
        }
    }

    // PF-2223: Retrieving paste position
    // might get complicated because of where is the control being pasted
    getPastePosition() {
        return;
    }

    cloneBehaviorConnections(conns, cloneMap, newConnections, render = false) {
        // get all connection from the original widget that was copied
        //let conns = sourceObject.model.get('widget').connection;
        const self = this;

        // COPY THE VO CONNECTION Object and modify the LIne Model for specific things
        if (conns) {
            _.forEach(conns, function(conn) {
                const sWidget = cloneMap.get(conn.srcCommand.model.get('id'));
                const dWidget = cloneMap.get(conn.destCommand.model.get('id'));
                const srcObj = self.getBehaviorObject(sWidget);
                const destObj = self.getBehaviorObject(dWidget);

                // create new VO object
                const connectionVO = new ConnectionVO( srcObj, destObj, conn.srcDot, conn.destDot );
                conn.model.attributes.id =  connectionVO.id;
                connectionVO.model = conn.model;
                // PF-4903 had destWidgetModel and src srcWidgetModel reversed here after copy/paste:
                connectionVO.model.attributes.destWidgetModel = destObj.model;
                connectionVO.model.attributes.srcWidgetModel = srcObj.model;
                connectionVO.srcCommand = srcObj;
                connectionVO.destCommand = destObj;

                if (conn.model.attributes.branchBooleanDisplay === 'true') {
                    srcObj.model.attributes.trueConnector = conn.model.get('id');
                }
                if (conn.model.attributes.branchBooleanDisplay === 'false') {
                    srcObj.model.attributes.falseConnector = conn.model.get('id');
                }

                self.addBehaviorConnection( connectionVO );
            });
        }
    }

    cloneBehaviorObject(sourceObject, render=false) {
        const factoryKey = sourceObject.model.get('factoryKey');
        const factoryParams = factoryService.behaviorsFactories[factoryKey];
        const dropPos = {
            left: util.extractNum(sourceObject.model.attributes.x),
            top: util.extractNum(sourceObject.model.attributes.y)
        };

        const control = factoryParams.factory.createView(factoryKey, dropPos);
        const newId = control.model.get('id');
        const newWidget = control.model.get('widget');
        control.model = sourceObject.model.clone();

        // PF-4827 model cloned  only level-one attributes.
        control.model.attributes = _.cloneDeep(sourceObject.model.attributes);

        //PF-8756 Update notifications Ids after paste to avoid duplicates
        if(factoryKey === "command_notify"){
            const notifications = control.model.get("notifications");

            if(notifications && Array.isArray(notifications)){
                notifications.forEach(n => {
                    n.notificationId = sequenceService.nextDomId();
                });  
            }                             
        }

        control.model.id = newId;
        control.model.attributes.id = newId;
        control.model.attributes.widget = newWidget;
        if (render) {
            control.render();
            control.model.get('widget').connection = [];
        }


        return control;
    }

    //========================================================
    // Clears clipboard. If an object on the clipboard
    // does not exist in the object list, then no more
    // object.
    //========================================================
    clearClipboard( ) {
        this._clipboard = this._clipboard.clear( );
    }

    get clipboardCount() { return  this._clipboard.size; }

    get clipboard() { return  this._clipboard; }

    //========================================================
    // Clears behavior clipboard.
    //========================================================
    clearBehaviorClipboard( ) {
        this._behaviorClipboard = undefined;
    }

    get behaviorClipboard() { return  this._behaviorClipboard; }

    clearWorkflowClipboard( ) {
        this._workflowClipboard = undefined;
    }

    get workflowClipboard() { return  this._workflowClipboard; }

    //========================================================
    // Pages
    //========================================================

    addPage( id, pageObj, options = {}  ) {
        this._pageList = this._pageList.set( id, pageObj );
        this.currentPageId = id;

        pageSyncService.addPageFromPageCollection(pageObj);

        if(options.undo){
           this.reorderPages(this._pageList.size - 1, options.pageIndex);
        }

        if (!options.silent) {
            //$(document).trigger(constants.EV_PAGES_UPDATED);
            eventBus.trigger(constants.EV_PAGES_UPDATED, {pageIndex: pageObj.currentIndex, undo: options.undo});
        }
    }

    //====================================================================
    // Removes the page object list. Note that a page is a BB view object,
    // not an ID. Its id is ts key in the list.
    //====================================================================
    deletePage( pageId ){
        this._pageList = this._pageList.delete( pageId );
        this._objMap = this._objMap.delete( pageId );

        this.deleteMobilePages(pageId);
        this.deleteMobileColumnsByPage(pageId);
        this.deleteMobileSectionsByPage(pageId);
        this.deleteMobileObjectsByPage(pageId);

        const newCurrentPage = this.pageList.last();

        //This is the new current pager
        pageInstanceFactory.pageInstanceView = newCurrentPage;

        this.currentPageId = newCurrentPage ? newCurrentPage.id : undefined;
        // Note that currentPageId could be undefined. That means we deleted all pages.

        const targetBehaviors = this.getBehaviorInstanceByPage(pageId);
        targetBehaviors.map( (instance, id) => {
            this.destroyBehaviorInstance(id);
            //$(document).trigger(constants.EV_PAGE_OBJECTS_UPDATED, ['delete','behavior', id, '', pageId ]);
        });

        pageSyncService.removePageFromPageCollection(pageId);

        //$(document).trigger(constants.EV_PAGES_UPDATED);
        eventBus.trigger(constants.EV_PAGES_UPDATED, {evt: "DELETE", pageId:pageId});
    }

    get currentPageId() {
        return this._currentPageId;
    }

    set currentPageId(id) {
        this._currentPageId = id;
    }

    clearPages( options={} ) {
        this._pageList = Immutable.OrderedMap();
        if ( !options.silent ) {
            //$(document).trigger(constants.EV_PAGES_UPDATED);
            eventBus.trigger(constants.EV_PAGES_UPDATED);
        }
    }


    // TODO Is this renaming the ID instead of the page name? -lem 2016-11-04
    renamePageId( existingId, newId ){
        const view = this._pageList.get( existingId );

        view.id = newId;
        view.model.set( 'id', newId);

        this._pageList = this._pageList.delete( existingId  ).set( newId, view );
        return this._pageList;
    }

	get pageList() {
        return this._pageList;
    }

    set pageList(aPageList ) { this._pageList = aPageList; }
    
    get mobilePages(){
        return this._mobilePages;
    }

    get pageCount() { return this.pageList.size }

    getPage(id) {
        return this._pageList.get( id );
    }

    getPageByOther( filter ) {
        let pages = this._pageList;
        if(filter){
            pages = pages.filter(filter);
        }

        return pages.first();
    }

    getPageIndex(pageId){
        
        let pageIndex = 0;
        let pages = this._pageList.toArray();
        pages.forEach(function(val, index) {
            if(val.id === pageId){
                pageIndex = index;
            }
        });

        return pageIndex;
    }

    reorderPages(fromIndex, toIndex) {

        let oldPages = this._pageList.toArray();
        let pageToMove = oldPages[fromIndex];
        // remove page from array
        oldPages.splice(fromIndex, 1);
        // add page back into array
        oldPages.splice(toIndex, 0, pageToMove);

        this.clearPages(  { silent: true } );

        _.forEach(oldPages, (page) => {
            this._pageList = this._pageList.set( page.id, page );
        });       

        eventBus.trigger(constants.EV_UPDATE_BEHAVIORS_ONLY);
    }

/*
    //=========================================================
    // Undo / redo. Delegate to UndoManager
    // https://github.com/ArthurClemens/Javascript-Undo-Manager
    // make undo-able
    //=========================================================

    // Add undo, redo action pair
    // E.g.
    // undoAdd.add({
    //     undo: function() {
    //         removeControl(id)
    //     },
    //     redo: function() {
    //         addControl(id, name);
    //     }
    // })
    //
    addUndo( undoRedoFnPair ){
        console.log('DEPRECATED - listService');
        this._undoManager.add( undoRedoFnPair );
    }

    // Execute current undo
    undo() {
        console.log('DEPRECATED - listService');
        this._undoManager.undo();
    }

    // Execute current redo
    redo() {
        console.log('DEPRECATED - listService');
        this._undoManager.redo();
    }

    // Clear the undo/redo stack
    clearUndo( options={} ) {
        console.log('DEPRECATED - listService');
        this._undoManager.clear( options );
    }

    // Set maximum number of undo steps. Limit = 0 == infinity
    setUndoLimit( limit = 0, options={} ){
        console.log('DEPRECATED - listService');
        this._undoManager.setLimit( limit, options );
    }

    // Are there undo actions?
    get hasUndo() { 
        console.log('DEPRECATED - listService');
        this._undoManager.hasUndo(); }


    // Are there redo actions?
    get hasRedo() { 
        console.log('DEPRECATED - listService');
        this._undoManager.hasRedo(); }


    // Notify callback on changes
    set listenForUndo( callBackFn ) { 
        console.log('DEPRECATED - listService');
        this._undoManager.setCallback( callBackFn );}


    // Get index of redo actions list
    get undoIndex() { 
        console.log('DEPRECATED - listService');
        this._undoManager.getIndex(); }
*/

    //=========================================================
    // Maintains the set of main view instances for
    // edit, behaviors, workflow ...
    //=========================================================
    addMainView( key, view ){
        this._mainViews = this._mainViews.set( key, view );
        return this._mainViews;
    }

    // Main view at key
    getMainView( key ) {
        return this._mainViews.get( key );
    }

    clearMainViews( options={} ) {
        this._mainViews = this._mainViews.clear( options );
        return this._mainViews;
    }

    get mainViews() { return this._mainViews; }

    get lastActiveElementInProperties() {
        return this._lastActiveElementInProperties;
    }
    set lastActiveElementInProperties(v) {
        this._lastActiveElementInProperties = v;
    }

    //==========================================================
    // Maintains forward-backward history of main views visited
    //==========================================================
    get mainHistory() { return this._mainHistory; }

    pushMainHistory( view ) {
        this._mainHistory = this._mainHistory.push( view );
        return this._mainHistory;
    }

    clearMainHistory( ) {
        this._mainHistory = this._mainHistory.clear();
        return this._mainHistory;
    }

    //==========================================================
    // Current Fabric canvas instance
    //==========================================================
    get fcanvas() { return this._fcanvas; }
    set fcanvas( arg )  {this._fcanvas = arg; }

    
    get behaviorPage() { return this._behaviorPage; }
    set behaviorPage( arg ) { this._behaviorPage = arg; }

    get workflowPage() { return this._workflowPage; }
    set workflowPage( arg ) { this._workflowPage = arg; }

    //==========================================================
    // Snap-to-grid option. PF-110
    //==========================================================
    get snapToGrid() { return this._snapToGrid; }
    set snapToGrid( snapToGrid ) { this._snapToGrid = snapToGrid; }

    get dashboardLayout() {
        return this._dashboardLayout;
    }

    //==========================================================
    // Snap-to-neightbor option. PF-111
    //==========================================================
    get snapToNeighbor() { return this._snapToNeighbor; }
    set snapToNeighbor( snapToNeighbor ) { this._snapToNeighbor = snapToNeighbor; }

    //==========================================================
    // Map encoded PF IDs to cleartext JS IDs. The PF IDs are
    // no DOM-friendly and are huge.
    //==========================================================
    resetCleanIds() {
        this._cleanIds = this._cleanIds.clear();
    }

    cleanId( id ){
        if ( this.isValidId(id) || id === undefined ){
            return id;
        }

        if ( !this._cleanIds.has( id )) {
            this._cleanIds = this._cleanIds.set(id, sequenceService.nextCleanId());
        }
        return this._cleanIds.get( id );
    }

    getCleanedId( id ) {
        let returnId = id;

        const cid = this._cleanIds.filter( v => v === id );
        if (cid.size > 0) {
            returnId = cid._root.entries[0][0];
        }
        return returnId;
    }

    // PF-2163
    // Function that retrieves an object referenced by a clean id.
    getObjectByCleanedId( cleanedId ) {
        return this._cleanIds.get(cleanedId) || cleanedId;
    }

    isValidId(id) {
        let valid = false;

        if (id) {
            if (id.length >= 4) {
                if (id.substring(0,4) === constants.ID_PREFIX) {
                    valid = true;
                }
            }
        }
        return valid;
    }

    //==========================================================
    // Reset 'em all. (except mainViews)
    //==========================================================
    reset(  options={} ) {
        this.clearObjectList( options );
        this.clearSelected( options );
        undoService.clear();
        this.clearClipboard( options );
        this.clearPages( options );
        this.resetAllConnections();
        this.resetCleanIds();
        this.resetDirty();
        this.clearWorkflow();
        this.isDrawingMultiSelection = false;
        this.isFromMultSelectionGroup = false;
        this.isResizing = false;
        this.clearBehaviorClipboard();
        this.clearWorkflowClipboard();
        this.clearBehaviorInstances();
        this.clearPages(  { silent: true } );
        this._currentBehaviorInstanceId = undefined;
        this._currentPageId = undefined;
        sequenceService.resetAll();
        this._selectFieldExpandedPages = Immutable.Map();
        this._dataSources = this._dataSources.clear();
        this._reportingMetaData = this._reportingMetaData.clear();
        this._dependencies = this._dependencies.clear();
        this.resetMobileObjects();
        mobileListService._mobileObjMap = Immutable.Map();
        // this.resetDocType();

        this.clearMultilanguage();
        this.clearAdvancedCSS();


        //this.currentForm = undefined;
        //this.formGuid = undefined;

        //this.clearMainViews( options );
        this.initMobileSections();
        this._mobileCurrentStep = 1;
        this._mobileVideoHasBeenSeen = false;
    }

    get userProfile() { return this._userProfile; }

    set userProfile(arg) {
        this._userProfile = arg;
    }

    get recoverySaveInterval() { return this._recoverySaveInterval; }

    set recoverySaveInterval(arg) {
        this._recoverySaveInterval = arg;
    }

    get userSettings() { return this._userSettings; }

    set userSettings(arg) {
        this._userSettings = arg;
        window.localStorage.setItem('pfUserSettings', JSON.stringify(arg));
        console.log('args here ++', arg);


        this._snapToGrid = this.userSettings.attributes['_edAl'] === '1';
        this._snapToNeighbor = util.isTrue( this.userSettings.attributes['_edShWfM'] );

    }

    get activeSession() { return this._activeSession; }

    set activeSession(arg) {
        this._activeSession = arg;
    }

    get activeSessionRetries() { return this._activeSessionRetries; }

    set activeSessionRetries(arg) {
        this._activeSessionRetries = arg;
    } 
    
    get activeSessionRetriesTimeout() { return this._activeSessionRetriesTimeout; }

    set activeSessionRetriesTimeout(arg) {
        this._activeSessionRetriesTimeout = arg;
    } 

    reorderTabIndexes(updatedControlId, deletedControl, tabIndexParentId) {

        return new Promise((resolve, reject) => {

            let updatedControl = this.getObject(updatedControlId);
            if (!updatedControl) {
                updatedControl = deletedControl;
            }
            sequenceService.tabIndexParentId = this.currentPageId;
            if (updatedControl && updatedControl.groupId) {
                // set tabIndexParent
                sequenceService.tabIndexParentId = updatedControl.groupId;
            }
            if (tabIndexParentId) {
                sequenceService.tabIndexParentId = tabIndexParentId;
            }
            const maxIndex = sequenceService.peekNextTabIndex() - 1;

            let filter;
            let updatedIndex = 0;
            let indexModified = false;

            if (updatedControl) {
                if (updatedControl.model.attributes.tabIndex < 1) {
                    updatedControl.model.attributes.tabIndex = 1;
                    indexModified = true;
                }
                if (updatedControl.model.attributes.tabIndex > maxIndex) {
                    updatedControl.model.attributes.tabIndex = maxIndex;
                    indexModified = true;
                }

                updatedIndex = updatedControl.model.attributes.tabIndex.toString();
            }

            let findByGroupId = (updatedControl && updatedControl._groupId) ? updatedControl._groupId : '';
            const forceUseThisParent = this.getObject(tabIndexParentId);
            let isForceIdGroup = false;
            if (forceUseThisParent && constants.GROUP_KEYS.contains(forceUseThisParent.model.get('factoryKey'))) {
                isForceIdGroup = true;
                findByGroupId = tabIndexParentId;
            }

            filter = (control) => {
                return (
                    (control.pageId && control.pageId === this.currentPageId && (!control._groupId))
                );
            };

            if (updatedControl && updatedControl._groupId || isForceIdGroup) {
                // set Filter
                filter = (control) => {
                    return (
                        (control.pageId && control.pageId === this.currentPageId && (control._groupId === findByGroupId))
                    );
                };
            }
            sequenceService.resetTabIndex();


            const objects = this.objectList
                .filter(filter)
                .sortBy((control) => -control.model.get('tabIndex'))
                .reverse()
                .map(control => {
                    if (updatedControl.id !== control.id) {
                        const nextTabIndex = sequenceService.peekNextTabIndex().toString();
                        if (updatedIndex === nextTabIndex && updatedControlId) {
                            const skip = sequenceService.nextTabIndex();
                        }
                        control.model.attributes.tabIndex = sequenceService.nextTabIndex();
                    }
                });

            resolve({indexModified: indexModified})

        });
    }

    combineSelectedModels() {
        const _model = new multiSelectControlModel();
        let i = 0,
            factoryKeys = [];

        this.selected.map((v,k) => {
            factoryKeys.push(v.model.get('factoryKey'));
            if (i === 0) {
                // stringify to remove the ref to               
                _model.attributes =  JSON.parse( JSON.stringify( v.model.toJSON()));
            } else {
                let vModel = JSON.parse( JSON.stringify( v.model.toJSON()));
                Immutable.List(v.model.keys())
                .map(mk => {
                    // PF-1891: Multiselect for links
                    if (mk === 'Parameters') {
                        if ( vModel[mk].Parameter && _model.get('Parameters') ) {
                            const oldValues = _model.get('Parameters');
                            if (oldValues.Parameter[0].literal !== vModel[mk].Parameter[0].literal) {
                                oldValues.Parameter[0].literal = '';
                            }
                            if (oldValues.Parameter[0].field !== vModel[mk].Parameter[0].field) {
                                oldValues.Parameter[0].field = '';
                            }
                            if (oldValues.Parameter[0].special !== vModel[mk].Parameter[0].special) {
                                oldValues.Parameter[0].special = '';
                            }
                            _model.set('Parameters', oldValues, constants.MODEL_SILENT);
                        }
                        return;
                    }

                    if (_model.attributes.hasOwnProperty(mk)) {
                        if (Array.isArray(_model.attributes[mk])) {                               
                            if (!_.isEqual(_model.attributes[mk], vModel[mk]) ) {
                                _model.attributes[mk] = [];  
                            }
                        }else{
                            if(_model.attributes[mk] !== vModel[mk]){
                                // PF-1891: When the model property already exists in the group, set up as default the last value
                                _model.attributes[mk] = null; 
                            }
                        }
                    } else {
                        _model.attributes[mk] = v.model.attributes[mk];
                    }
                });
            }
            i++;
            });

        // PF-1891: Special Scenarios - hide alignment section when:
        //      link + text-type controls
        //      or bullet text + text-type controls
        //      or link + bullet
        _model.attributes['hideAlignmentSection'] = ( _.includes(factoryKeys, 'control_link') && _.intersection(constants.TEXT_CONTROLS_KEYS, factoryKeys).length ) ||
            ( _.includes(factoryKeys, 'control_bullet_text') && _.intersection(constants.TEXT_CONTROLS_KEYS, factoryKeys).length ) ||
            ( _.includes(factoryKeys, 'control_link') && _.includes(factoryKeys, 'control_bullet_text') );

        if (_.includes(factoryKeys, 'control_numeric_input') ) {
            _model.attributes.text = _model.attributes.text || '0.00';
        }

        if (_.includes(factoryKeys, 'control_date_input') && _.intersection(constants.BUTTON_CONTROLS_KEYS, factoryKeys).length ) {
            _model.attributes.hideColor = 'hide';
        }

        if (_.includes(factoryKeys, 'control_drop_down') && _.intersection(constants.BUTTON_CONTROLS_KEYS, factoryKeys).length ) {
            _model.attributes.hideColor = 'hide';
        }

        if (_.includes(factoryKeys, 'control_date_input') && _.includes(factoryKeys, 'control_progress_bar')) {
            _model.attributes.hideRadius = 'hide';
        }

        if (_.intersection(factoryKeys, ['control_play_form_button','control_play_report_button']).length) {
            _model.attributes.CustomParameters = {parameter:[]};
        } 

        return this.parseArrayToMap(_model);
    }
    
    determineFieldName(id) {
        let concatIds = [];
        let nameSuffix = '';
        let name = '';
        let obj;
        let objId = id;
        let isMultipleSelection = false;

        if (objId.includes('$')) {
            let objs = objId.split('$');
            obj = this.getObject( this.cleanId(objs[0]) );
            if( obj && (obj.model.get('factoryKey') === 'control_ranking' || (obj.model.get('factoryKey') === 'control_list' && util.isTrue(obj.model.get('multiSelection'))))){
                isMultipleSelection = true;
                const index = parseInt(objs[1]);
                if(obj.model.get('gridData').length > 0){
                    nameSuffix = obj.model.get('gridData')[index].item || obj.model.get('gridData')[index].caption;
                }
            }
        }

        if (objId.includes('$cuc_') ) {
            concatIds = objId.split('$cuc_');
            nameSuffix = ' - Current Row - Caption';
        } else if (objId.includes('$alc_') ) {
            concatIds = objId.split('$alc_');
            nameSuffix = ' - All Rows - Caption';
        } else if (objId.includes('$cur_') ) {
            concatIds = objId.split('$cur_');
            nameSuffix = ' - Current Row';
        } else if (objId.includes('$all_') ) {
            concatIds = objId.split('$all_');
            nameSuffix = ' - All Rows';
        } else if (objId.includes('$v')) {
            if (obj && obj.model.get('factoryKey') === 'control_file_attachment'){
                nameSuffix = ' - Attachment Name';
            }else{
                nameSuffix = ' - Value';
            }

            objId = objId.replace('$v','');
        }else if (objId.includes('$d')) {
            objId = objId.replace('$d','');
            nameSuffix = ' - Attachment File';
        }
        else if (objId.includes('$f')) {
            objId = objId.replace('$f','');
            nameSuffix = ' - Attachment File';
        }

        if (objId.includes('$cur_') || objId.includes('$all_')) { //PF-8939 - Table - Drop-down column losing caption/value postfix
            if (obj && obj.model.get('gridData') && concatIds.length === 2) {
                let _self = this;
                _.forEach(obj.model.get('gridData'), function (col) {
                    if (col.get('id') === _self.cleanId(concatIds[1]) && col.get('type') === constants.CONTROL_TYPE_TABLE_DROPDOWN) {
                        nameSuffix += ' - Value';
                        return false;
                    }
                });
            }
        }

        if (concatIds.length > 0) {
            obj = this.getObject( this.cleanId(concatIds[0]) );
            if (obj) {
                name = obj.model.get('name');
                // find column info in model (GridData )
                _.forEach(obj.model.get('gridData'), (col) => {
                    if (col.get('id') === this.cleanId(concatIds[1])) {
                        name += ' - ' + col.get('name');
                    }
                });
                name += nameSuffix;
            }
        }else if(isMultipleSelection){
            name = obj.model.get('name') + ' - ' + nameSuffix;
        } else {
            obj = this.getObject(this.cleanId(objId));
            if (obj) {
                if (constants.CONTROL_CAPTION_VALUE_KEYS.contains(obj.model.get('factoryKey')) && (nameSuffix === '')) {
                    nameSuffix = ' - Caption';
                }
                name = obj.model.get('name') + nameSuffix;
            }
        }


        return name;
    }

    getGroupObject(id){
        let control = this.getObject(id);
        return control && control._groupId ? control._groupId : '';
    }
    
    isGroup(id){
        let control = this.getObject(id);
        if(control){
            return constants.GROUP_KEYS.contains( control.model.get('factoryKey') );
        }
    }

    // PF-2524: Calculate & Update parent's minimu width and height 
    // based on the group's children. Children might have be added or removed
    calculateGroupDimensions( groupId, childId ) {
        const parendGroupId = groupId || this.getGroupObject(childId);
        if (parendGroupId) {
            const getParentGroup = this.getObject(parendGroupId);
            let newMinWidth = 1;
            let newMinHeight = 1;
            getParentGroup.model.get('childList').forEach(o=>{
                const tmpWidth = util.extractNum(o.get('left')) + util.extractNum(o.get('width'));
                const tmpHeight = util.extractNum(o.get('top')) + util.extractNum(o.get('height'));
                if (tmpWidth > newMinWidth) {
                    newMinWidth = tmpWidth;
                }
                if (tmpHeight > newMinHeight) { 
                    newMinHeight = tmpHeight;
                }
            });

            getParentGroup.model.set('minWidth', newMinWidth, {silent: true});
            getParentGroup.model.set('minHeight', newMinHeight, {silent: true});
        }
    }

    // PF-2322: Getting all valid fabricjs canvas elements
    // valid as in: workflow controls stage and comment
    getValidCanvasObjects( objs = this.fcanvas.getObjects() ) {
        return objs.filter(o=>{
            if (o.active && o.model) {
                // return valid objects for workflow view OR behaviors view
                return (
                    this.mainKind === constants.KIND_BEHAVIORS && o.model.get('factoryKey') !== constants.CONNECTION_FACTORY_KEY
                ) || (
                    this.mainKind === constants.KIND_WORKFLOW && constants.WORKFLOW_FACTORY_KEYS.contains(o.model.get('factoryKey'))
                );
            } });
    }

    // PF-2320: helper method to fill up selectedCount variable
    // when showing canvas (behaviors+workflow)
    countingValidObjects() {
        this.fcanvas.getObjects().filter(o=>{ 
            if( o.active && o.model && ( (this.mainKind === constants.KIND_WORKFLOW && constants.WORKFLOW_FACTORY_KEYS.contains(o.model.get('factoryKey'))) || (this.mainKind === constants.KIND_BEHAVIORS && o.model.get('factoryKey') !== constants.CONNECTION_FACTORY_KEY ) )) {
                this.addToSelected(o.model.get('id'));
            }
        });
    }

    parseArrayToMap(model){
        if (model.attributes['mandatories']){
          model.attributes['mandatories'] = new Map(Object.keys(model.attributes['mandatories']));
        }
        if (model.attributes['state']){
          model.attributes['state'] = new Map(Object.keys(model.attributes['state']));
        }
        return model;
      }

    unsetPageOriginalItems(pageId){
        const page = this.getPage(pageId);
        if (page && typeof page.model.get('parsedItem') !== 'undefined') {
            page.model.unset('parsedItem', {unsetParsedItem: true});
        }
    }
    
    isSSO(){
        return this._gatewayUser ? "&ut="+window.btoa( "&u="+this._gatewayUser) : "";
    }
}

const listService = new ListService();
export default listService;